import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/config';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Computer as ComputerIcon,
  Language as LanguageIcon,
  ScreenLockPortrait as ScreenIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Extension as ExtensionIcon,
  Fingerprint as FingerprintIcon,
  Info as InfoIcon,
  NetworkCheck as NetworkIcon,
  LocationOn as LocationIcon,
  Storage as StorageIcon,
  Psychology as PsychologyIcon,
  Warning as WarningIcon,
  Explore as ExploreIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

// AnimatedWord Component für Merksatz
const AnimatedWord = ({ word, delay }) => {
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setHighlighted(true);
      setTimeout(() => setHighlighted(false), 1000);
    }, 6000 + delay);

    return () => clearInterval(interval);
  }, [delay]);

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        fontWeight: highlighted ? 700 : 400,
        fontSize: highlighted ? '1.3rem' : '1.1rem',
        transition: 'all 0.5s ease',
        transform: highlighted ? 'scale(1.1)' : 'scale(1)',
      }}
    >
      {word}
    </Box>
  );
};

const BrowserInfo = () => {
  const navigate = useNavigate();
  const { user, getToken, updateUser } = useAuth();
  const [consentGiven, setConsentGiven] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [topRiskOpen, setTopRiskOpen] = useState(null);
  const [additionalRiskTab, setAdditionalRiskTab] = useState('preise');
  const [viewedRisks, setViewedRisks] = useState(new Set());
  const [plainLanguageToggle, setPlainLanguageToggle] = useState({});
  const [tocValue, setTocValue] = useState(0);
  const isScrollingRef = useRef(false);
  const [browserInfo, setBrowserInfo] = useState(null);
  const [canvasFingerprint, setCanvasFingerprint] = useState(null);
  const [webglInfo, setWebglInfo] = useState(null);
  const [audioFingerprint, setAudioFingerprint] = useState(null);
  const [fonts, setFonts] = useState([]);
  const [storageInfo, setStorageInfo] = useState(null);
  const [behaviorInfo, setBehaviorInfo] = useState(null);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationError, setLocationError] = useState(null);
  const [scrollDepth, setScrollDepth] = useState(0);
  const [clientHints, setClientHints] = useState(null);
  const [browserVersion, setBrowserVersion] = useState(null);
  const [botInfo, setBotInfo] = useState(null);
  const [vpnInfo, setVpnInfo] = useState(null);
  const [extensionInfo, setExtensionInfo] = useState(null);
  const [startTime] = useState(Date.now());
  const maxScrollRef = useRef(0);

  // Scroll detection for TOC highlighting
  useEffect(() => {
    if (!browserInfo) return; // Wait for sections to be rendered

    const sections = [
      'welcome',
      'steckbrief',
      'nutzung',
      'risiken',
      'empfehlungen',
      'nerd-stuff'
    ];

    const handleScroll = () => {
      // Don't update if user is clicking on tabs (programmatic scroll)
      if (isScrollingRef.current) return;

      const scrollPosition = window.scrollY + 200; // Offset for sticky header

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element) {
          const offsetTop = element.offsetTop;
          if (scrollPosition >= offsetTop) {
            setTocValue(i);
            break;
          }
        }
      }
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [browserInfo]); // Re-run when browserInfo is available (sections are rendered)

  // Load user profile data if first_name is missing
  useEffect(() => {
    const loadUserProfile = async () => {
      // Only load if user exists but first_name is missing
      if (user && !user.first_name) {
        try {
          const token = getToken();
          if (!token) return;

          const response = await fetch(`${API_CONFIG.BASE_URL}/auth/profile`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const profileData = await response.json();
            updateUser(profileData);
          }
        } catch (err) {
          console.error('Error loading user profile:', err);
        }
      }
    };

    loadUserProfile();
  }, [user, getToken, updateUser]);

  useEffect(() => {
    collectBrowserInfo();
    collectBehaviorData();
    fetchLocationData();
    collectClientHints();
    checkBrowserVersion();
    detectBot();
    detectVPN();
    detectExtensions();
    
    // Setup scroll tracking
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      
      if (scrollHeight > clientHeight) {
        const scrollPercent = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);
        maxScrollRef.current = Math.max(maxScrollRef.current, scrollPercent);
        setScrollDepth(maxScrollRef.current);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Update behavior data periodically
    const interval = setInterval(() => {
      collectBehaviorData();
    }, 1000);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, []);

  const collectBrowserInfo = () => {
    // Get platform from userAgentData if available (modern API), otherwise parse from userAgent
    let platform = 'Nicht verfügbar';
    if (navigator.userAgentData && navigator.userAgentData.platform) {
      platform = navigator.userAgentData.platform;
    } else if (navigator.platform) {
      platform = navigator.platform;
    }

    // Get vendor from userAgentData if available
    let vendor = 'Nicht verfügbar';
    if (navigator.userAgentData && navigator.userAgentData.brands && navigator.userAgentData.brands.length > 0) {
      vendor = navigator.userAgentData.brands.map(b => b.brand).join(', ');
    } else if (navigator.vendor) {
      vendor = navigator.vendor;
    }

    // Connection information
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      setConnectionInfo({
        effectiveType: connection.effectiveType || 'Nicht verfügbar',
        downlink: connection.downlink || 'Nicht verfügbar',
        rtt: connection.rtt || 'Nicht verfügbar',
        saveData: connection.saveData || false,
      });
    }

    // Storage information
    try {
      const localStorageSize = JSON.stringify(localStorage).length;
      const sessionStorageSize = JSON.stringify(sessionStorage).length;
      const localStorageKeys = Object.keys(localStorage).length;
      const sessionStorageKeys = Object.keys(sessionStorage).length;
      
      // Cookie details
      const cookies = document.cookie;
      const cookieCount = cookies ? cookies.split(';').filter(c => c.trim()).length : 0;
      
      setStorageInfo({
        localStorageAvailable: typeof Storage !== 'undefined',
        localStorageSize: localStorageSize,
        localStorageKeys: localStorageKeys,
        sessionStorageAvailable: typeof sessionStorage !== 'undefined',
        sessionStorageSize: sessionStorageSize,
        sessionStorageKeys: sessionStorageKeys,
        indexedDBAvailable: 'indexedDB' in window,
        cacheAvailable: 'caches' in window,
        serviceWorkersAvailable: 'serviceWorker' in navigator,
        cookiesEnabled: navigator.cookieEnabled,
        cookieCount: cookieCount,
        cookies: cookies ? cookies.substring(0, 200) + (cookies.length > 200 ? '...' : '') : 'Keine',
      });
    } catch (e) {
      console.error('Storage info error:', e);
    }

    // Basic browser information
    const info = {
      // User Agent & Platform
      userAgent: navigator.userAgent,
      platform: platform,
      vendor: vendor,
      
      // Language & Locale
      language: navigator.language,
      languages: navigator.languages || [],
      
      // Screen & Display
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      screenColorDepth: window.screen.colorDepth,
      screenPixelDepth: window.screen.pixelDepth,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
      
      // Timezone
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      currentTime: new Date().toISOString(),
      
      // Hardware
      hardwareConcurrency: navigator.hardwareConcurrency || 'Nicht verfügbar',
      maxTouchPoints: navigator.maxTouchPoints || 0,
      deviceMemory: navigator.deviceMemory || 'Nicht verfügbar',
      
      // Screen details
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      orientation: window.screen.orientation 
        ? `${window.screen.orientation.angle}° (${window.screen.orientation.type})` 
        : 'Nicht verfügbar',
      
      // Features
      cookiesEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      touchSupport: 'ontouchstart' in window,
      batteryAPI: 'getBattery' in navigator,
      geolocationAPI: 'geolocation' in navigator,
      
      // URL & Referrer
      currentURL: window.location.href,
      protocol: window.location.protocol,
      host: window.location.host,
      pathname: window.location.pathname,
      search: window.location.search || '(keine)',
      hash: window.location.hash || '(keine)',
      referrer: document.referrer || 'Direkter Zugriff (kein Referrer)',
      
      // Connection type
      connectionType: connection ? (connection.type || 'Unbekannt') : 'Nicht verfügbar',
      effectiveType: connection ? (connection.effectiveType || 'Unbekannt') : 'Nicht verfügbar',
      
      // Security
      https: window.location.protocol === 'https:',
      secureContext: window.isSecureContext,
      
      // Locale
      locale: Intl.DateTimeFormat().resolvedOptions().locale,
      numberingSystem: Intl.DateTimeFormat().resolvedOptions().numberingSystem,
    };

    // Canvas Fingerprint
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 200;
      canvas.height = 50;
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Browser-Fingerprint 🔒', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Browser-Fingerprint 🔒', 4, 17);
      const canvasData = canvas.toDataURL();
      setCanvasFingerprint(canvasData);
    } catch (e) {
      console.error('Canvas fingerprint error:', e);
    }

    // WebGL Information
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        setWebglInfo({
          vendor: gl.getParameter(gl.VENDOR),
          renderer: gl.getParameter(gl.RENDERER),
          version: gl.getParameter(gl.VERSION),
          shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
          debugVendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'Nicht verfügbar',
          debugRenderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'Nicht verfügbar',
        });
      }
    } catch (e) {
      console.error('WebGL info error:', e);
    }

    // AudioContext Fingerprint
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const analyser = audioContext.createAnalyser();
      const gainNode = audioContext.createGain();
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);

      gainNode.gain.value = 0;
      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.start(0);
      scriptProcessor.onaudioprocess = (event) => {
        const output = event.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < output.length; i++) {
          sum += Math.abs(output[i]);
        }
        const fingerprint = sum.toString();
        setAudioFingerprint(fingerprint);
        oscillator.stop();
        audioContext.close();
      };
    } catch (e) {
      console.error('Audio fingerprint error:', e);
    }

    // Font Detection
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const h = document.getElementsByTagName('body')[0];
    const s = document.createElement('span');
    s.style.fontSize = testSize;
    s.innerHTML = testString;
    const defaultWidth = {};
    const defaultHeight = {};

    for (let i = 0; i < baseFonts.length; i++) {
      s.style.fontFamily = baseFonts[i];
      h.appendChild(s);
      defaultWidth[baseFonts[i]] = s.offsetWidth;
      defaultHeight[baseFonts[i]] = s.offsetHeight;
      h.removeChild(s);
    }

    const detectedFonts = [];
    const testFonts = [
      'Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia',
      'Palatino', 'Garamond', 'Bookman', 'Comic Sans MS', 'Trebuchet MS',
      'Impact', 'Arial Black', 'Impact', 'Tahoma', 'Century Gothic',
      'Lucida Sans Unicode', 'Lucida Console', 'Courier', 'Monaco',
      'Helvetica', 'Roboto', 'Open Sans', 'Lato', 'Montserrat'
    ];

    for (let i = 0; i < testFonts.length; i++) {
      let detected = false;
      for (let j = 0; j < baseFonts.length; j++) {
        s.style.fontFamily = testFonts[i] + ',' + baseFonts[j];
        h.appendChild(s);
        const matched = (s.offsetWidth !== defaultWidth[baseFonts[j]] || 
                        s.offsetHeight !== defaultHeight[baseFonts[j]]);
        h.removeChild(s);
        if (matched) {
          detected = true;
          break;
        }
      }
      if (detected) {
        detectedFonts.push(testFonts[i]);
      }
    }

    setFonts(detectedFonts);
    setBrowserInfo(info);
  };


  const collectBehaviorData = () => {
    const behavior = {};
    
    // Navigation Timing API
    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      behavior.navigationStart = new Date(timing.navigationStart).toLocaleTimeString('de-DE');
      behavior.domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
      behavior.pageLoad = timing.loadEventEnd - timing.navigationStart;
      behavior.dnsLookup = timing.domainLookupEnd - timing.domainLookupStart;
      behavior.tcpConnect = timing.connectEnd - timing.connectStart;
      behavior.serverResponse = timing.responseStart - timing.requestStart;
    }
    
    // Performance Memory (Chrome)
    if (window.performance && window.performance.memory) {
      const mem = window.performance.memory;
      behavior.jsHeapSize = Math.round(mem.usedJSHeapSize / 1048576);
      behavior.jsHeapLimit = Math.round(mem.jsHeapSizeLimit / 1048576);
    }
    
    // Page Visibility
    behavior.pageHidden = document.hidden !== undefined ? document.hidden : 'Nicht verfügbar';
    behavior.visibilityState = document.visibilityState || 'Nicht verfügbar';
    
    // Time on page
    const timeOnPage = Math.round((Date.now() - startTime) / 1000);
    behavior.timeOnPage = timeOnPage;
    
    // Scroll depth - use ref to get current value
    behavior.scrollDepth = maxScrollRef.current;
    
    // Performance API availability
    behavior.performanceAPI = 'performance' in window;
    behavior.performanceObserver = 'PerformanceObserver' in window;
    
    setBehaviorInfo(behavior);
  };

  const collectClientHints = async () => {
    const hints = {};
    
    // User-Agent Client Hints (UA-CH)
    if (navigator.userAgentData) {
      const uaData = navigator.userAgentData;
      
      if (uaData.brands && uaData.brands.length > 0) {
        hints.brands = uaData.brands.map(b => `${b.brand} v${b.version}`).join(', ');
      }
      
      hints.mobile = uaData.mobile ? 'true (Mobilgerät)' : 'false (Desktop)';
      
      if (uaData.platform) {
        hints.platform = uaData.platform;
      }
      
      if (uaData.platformVersion) {
        hints.platformVersion = uaData.platformVersion;
      }
      
      // High-Entropy-Values
      if (uaData.getHighEntropyValues) {
        try {
          const values = await uaData.getHighEntropyValues(['architecture', 'bitness', 'model', 'uaFullVersion', 'wow64']);
          if (values.architecture) hints.architecture = values.architecture;
          if (values.bitness) hints.bitness = values.bitness;
          if (values.model) hints.model = values.model;
          if (values.uaFullVersion) hints.uaFullVersion = values.uaFullVersion;
          if (values.wow64 !== undefined) hints.wow64 = values.wow64 ? 'true' : 'false';
        } catch (e) {
          hints.highEntropyValues = 'Nicht verfügbar';
        }
      }
    } else {
      hints.userAgentDataAPI = 'Nicht verfügbar (nur Chrome/Edge)';
    }
    
    // Device Client Hints
    if (navigator.deviceMemory) {
      hints.deviceMemory = `${navigator.deviceMemory} GB`;
    }
    
    // Network Client Hints
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      if (conn.effectiveType) hints.ect = conn.effectiveType;
      if (conn.downlink) hints.downlink = `${conn.downlink} Mbps`;
      if (conn.rtt) hints.rtt = `${conn.rtt} ms`;
      if (conn.saveData !== undefined) hints.saveData = conn.saveData ? 'true' : 'false';
    }
    
    // Display Client Hints
    hints.viewportWidth = `${window.innerWidth}px`;
    if (window.devicePixelRatio) {
      hints.dpr = window.devicePixelRatio.toString();
    }
    
    // Preference Client Hints
    if (window.matchMedia) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const prefersLight = window.matchMedia('(prefers-color-scheme: light)').matches;
      if (prefersDark) hints.prefersColorScheme = 'dark';
      else if (prefersLight) hints.prefersColorScheme = 'light';
      
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      hints.prefersReducedMotion = prefersReducedMotion ? 'reduce' : 'no-preference';
    }
    
    setClientHints(hints);
  };

  const checkBrowserVersion = async () => {
    const ua = navigator.userAgent;
    let browser = 'Unbekannt';
    let version = 'Unbekannt';
    
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Opera')) browser = 'Opera';
    else if (ua.includes('Brave')) browser = 'Brave';
    
    const match = ua.match(/(?:Firefox|Chrome|Safari|Edg|Opera|Version)\/(\d+)/);
    if (match) version = match[1];
    
    setBrowserVersion({ browser, version, status: 'Prüfung nicht verfügbar' });
  };

  const detectBot = () => {
    const botInfo = {
      isBot: false,
      indicators: []
    };
    
    // WebDriver Property
    if (navigator.webdriver) {
      botInfo.isBot = true;
      botInfo.indicators.push('WebDriver-Property gesetzt');
    }
    
    // User-Agent Bot-Hinweise
    const userAgent = navigator.userAgent.toLowerCase();
    const botKeywords = ['bot', 'crawler', 'spider', 'scraper', 'headless', 'phantom', 'selenium', 'puppeteer', 'playwright'];
    const foundBotKeyword = botKeywords.find(keyword => userAgent.includes(keyword));
    if (foundBotKeyword) {
      botInfo.isBot = true;
      botInfo.indicators.push(`Bot-Hinweis im User-Agent: ${foundBotKeyword}`);
    }
    
    // Chrome ohne Chrome-Features
    if (navigator.userAgent.includes('Chrome') && !window.chrome) {
      botInfo.isBot = true;
      botInfo.indicators.push('Chrome ohne chrome-Objekt');
    }
    
    // Fehlende Plugins
    if (navigator.plugins && navigator.plugins.length === 0 && navigator.userAgent.includes('Chrome')) {
      botInfo.indicators.push('Keine Plugins (ungewöhnlich für Chrome)');
    }
    
    setBotInfo(botInfo);
  };

  const detectVPN = () => {
    const vpnInfo = {
      detected: false,
      indicators: [],
      confidence: 'niedrig'
    };
    
    // Zeitzone vs. Browser-Sprache Diskrepanz
    const timezone = browserInfo?.timezone;
    const language = browserInfo?.language;
    
    if (timezone && language) {
      // Vereinfachte Prüfung
      const timezoneCountry = timezone.includes('Europe') ? 'EU' : timezone.includes('America') ? 'US' : null;
      const languageCountry = language.startsWith('de') ? 'DE' : language.startsWith('en') ? 'US' : null;
      
      if (timezoneCountry && languageCountry && timezoneCountry !== languageCountry) {
        vpnInfo.indicators.push(`Zeitzone (${timezone}) passt nicht zur Browser-Sprache (${language})`);
        vpnInfo.detected = true;
        vpnInfo.confidence = 'mittel';
      }
    }
    
    // Tor Browser Detection
    if (navigator.userAgent.includes('Tor') || window.Tor) {
      vpnInfo.indicators.push('Tor Browser erkannt');
      vpnInfo.detected = true;
      vpnInfo.confidence = 'hoch';
    }
    
    setVpnInfo(vpnInfo);
  };

  const detectExtensions = () => {
    const detected = [];
    
    // AdBlock Detection
    if (window.getComputedStyle && document.body) {
      try {
        const testDiv = document.createElement('div');
        testDiv.className = 'pub_300x250 pub_300x250m pub_728x90 text-ad textAd text_ad text_ads text-ads text-ad-links';
        testDiv.style.cssText = 'position: absolute; top: -999px; width: 1px; height: 1px;';
        document.body.appendChild(testDiv);
        const computed = window.getComputedStyle(testDiv);
        if (computed.display === 'none' || computed.visibility === 'hidden' || computed.height === '0px') {
          detected.push('AdBlocker (wahrscheinlich)');
        }
        document.body.removeChild(testDiv);
      } catch (e) {}
    }
    
    // LastPass
    if (window._lphost || document.querySelector('#lptopframe') || window.__lp) {
      detected.push('LastPass');
    }
    
    // 1Password
    if (window.__1password || document.querySelector('[data-1p-ignore]')) {
      detected.push('1Password');
    }
    
    // Grammarly
    if (window.grammarly || document.querySelector('[data-grammarly-shadow-root]')) {
      detected.push('Grammarly');
    }
    
    // React DevTools
    if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      detected.push('React DevTools');
    }
    
    setExtensionInfo({ detected, count: detected.length });
  };

  const fetchLocationData = async () => {
    try {
      setLocationLoading(true);
      setLocationError(null);
      // Use backend API which will detect the client's IP address
      const response = await fetch('/api/v1/browser-info/location');
      if (!response.ok) {
        throw new Error('API-Anfrage fehlgeschlagen');
      }
      const data = await response.json();
      setLocationData(data);
    } catch (error) {
      console.error('Error fetching location data:', error);
      setLocationError('Standortdaten konnten nicht geladen werden. Möglicherweise blockiert Ihr Browser die Anfrage oder die API ist nicht verfügbar.');
    } finally {
      setLocationLoading(false);
    }
  };

const InfoCard = ({ title, icon: Icon, children, color = 'primary', emoji }) => (
  <Card elevation={2} sx={{ 
    height: '100%',
    background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 2,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      boxShadow: '0 12px 40px rgba(33, 150, 243, 0.4)',
      border: '1px solid rgba(66, 165, 245, 0.3)',
    },
  }}>
    <CardHeader
      avatar={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {emoji && <Typography variant="h6">{emoji}</Typography>}
          <Icon sx={{ color: '#42a5f5' }} />
        </Box>
      }
      title={<Typography variant="h6" sx={{ color: '#ffffff' }}>{title}</Typography>}
      sx={{ 
        background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.8) 0%, rgba(15, 15, 15, 0.8) 100%)',
        color: '#ffffff',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    />
    <CardContent sx={{ color: '#ffffff' }}>{children}</CardContent>
  </Card>
);

  const InfoRow = ({ label, value, chip = false }) => (
    <ListItem>
      <ListItemText
        primary={<Typography variant="body2" sx={{ color: '#ffffff' }}>{label}</Typography>}
        secondary={
          chip ? (
            <Chip label={value} size="small" color="primary" variant="outlined" />
          ) : (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {value}
            </Typography>
          )
        }
      />
    </ListItem>
  );

  const generateWelcomeMessage = () => {
    if (!browserInfo) return null;

    const textParts = [];
    const now = new Date();
    
    // Datum & Uhrzeit
    const dateTimeFormatted = now.toLocaleDateString('de-DE', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    textParts.push(`Wie schön, dass du heute am <strong>${dateTimeFormatted}</strong> bei uns vorbei schaust.`);

    // Bundesland & Location
    const bundesland = locationData?.location?.state || '';
    const city = locationData?.location?.city || '';
    const country = locationData?.location?.country || '';
    let locationForWeather = '';
    
    if (city && country) {
      locationForWeather = `${city}, ${country}`;
    } else if (city) {
      locationForWeather = city;
    } else if (country) {
      locationForWeather = country;
    }

    if (bundesland) {
      textParts.push(`Wie ich sehe kommst du aus <strong>${bundesland}</strong>.`);
      if (locationForWeather) {
        textParts.push(`Wie ist das Wetter aktuell in der Nähe von <strong>${locationForWeather}</strong>?`);
      }
    } else if (locationForWeather) {
      textParts.push(`Wie ist das Wetter aktuell in der Nähe von <strong>${locationForWeather}</strong>?`);
    }

    // OS, Browser, Sprache
    const os = browserInfo.platform || 'Unbekannt';
    const browser = detectBrowser();
    const language = browserInfo.language || 'Unbekannt';
    const languageName = getLanguageName(language);
    
    textParts.push(`Du nutzt gerade ein Gerät mit <strong>${os}</strong> Betriebssystem und installiertem <strong>${browser}</strong>-Browser in der <strong>${languageName}</strong> Spracheinstellung – das sieht nach einer angenehmen Arbeitsumgebung aus.`);

    // Gerätetyp
    const isMobile = /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) || 
                     (browserInfo.screenWidth <= 1024 && browserInfo.touchSupport);
    const deviceTypeText = isMobile ? 'Mobilgerät' : 'Desktop-Computer';
    textParts.push(`Lass mich raten, das ist ein <strong>${deviceTypeText}</strong>, oder?`);

    // Bildschirmauflösung
    const screenWidth = browserInfo.screenWidth;
    const screenHeight = browserInfo.screenHeight;
    const screenResolution = `${screenWidth}×${screenHeight}`;
    
    if (isMobile) {
      textParts.push(`Deine Bildschirmauflösung von <strong>${screenResolution} Pixeln</strong> zeigt, dass du ein <strong>Mobilgerät</strong> verwendest.`);
    } else {
      let monitorDescription = '';
      if (screenWidth >= 2560) {
        monitorDescription = 'großen Monitor (WQHD oder höher)';
      } else if (screenWidth >= 1920) {
        monitorDescription = 'größeren Monitor (Full HD)';
      } else if (screenWidth >= 1366) {
        monitorDescription = 'Standard-Monitor';
      } else {
        monitorDescription = 'kleineren Bildschirm';
      }
      textParts.push(`Deine Bildschirmauflösung von <strong>${screenResolution} Pixeln</strong> verrät, dass du wahrscheinlich an einem ${monitorDescription} sitzt.`);
    }

    // Verbindung & ISP
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const connectionType = connection?.type || 'unbekannt';
    const effectiveType = connection?.effectiveType || null;
    const downlink = connection?.downlink || null;
    const rtt = connection?.rtt || null;
    
    const connectionTypeMap = {
      'cellular': '4g',
      'wifi': 'WLAN',
      'ethernet': 'LAN',
      'bluetooth': 'Bluetooth',
      'wimax': 'WiMAX',
      'none': 'keine Verbindung'
    };
    const connectionTypeText = connectionTypeMap[connectionType] || connectionType;
    
    const connectionInfo = [];
    if (connectionType !== 'unbekannt' && connectionTypeText !== 'unbekannt') {
      connectionInfo.push(connectionTypeText);
    }
    if (effectiveType) {
      connectionInfo.push(`${effectiveType} Verbindungsqualität`);
    }
    if (downlink) {
      connectionInfo.push(`${downlink} Mbps Bandbreite`);
    }
    if (rtt) {
      connectionInfo.push(`${rtt} ms Latenz`);
    }
    
    if (connectionInfo.length > 0) {
      textParts.push(`Wie ich sehe bist du über <strong>${connectionInfo.join(', ')}</strong> verbunden.`);
    }

    // ISP
    const isp = locationData?.asn?.org || locationData?.company?.name || '';
    if (isp && isp !== 'Nicht verfügbar') {
      textParts.push(`Ich hoffe mit der Verbindung über <strong>${isp}</strong> ist alles in Ordnung?`);
    }

    // TLS
    const isHttps = browserInfo.https && browserInfo.secureContext;
    if (isHttps) {
      textParts.push(`Deine <strong>TLS-Verschlüsselung</strong> lässt zumindest darauf schließen, dass die Verbindung sicher ist.`);
    } else {
      textParts.push(`Leider fehlt eine <strong>TLS-Verschlüsselung</strong> – die Verbindung ist nicht verschlüsselt.`);
    }

    // AdBlocker & DNT
    const hasAdBlocker = extensionInfo?.detected?.some(ext => ext.includes('AdBlocker')) || false;
    const hasDNT = navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes';
    
    if (hasAdBlocker && hasDNT) {
      textParts.push(`Wie ich sehe nutzt du bereits <strong>einen AdBlocker</strong> und die <strong>"Do Not Track"-Direktive</strong>. Das ist sehr gut für deine Privatsphäre!`);
    } else if (hasAdBlocker) {
      textParts.push(`Wie ich sehe nutzt du bereits <strong>einen AdBlocker</strong>, aber <strong>keine "Do Not Track"-Direktive</strong>. Der AdBlocker ist gut, aber die "Do Not Track"-Direktive würde zusätzlich helfen.`);
    } else if (hasDNT) {
      textParts.push(`Wie ich sehe nutzt du die <strong>"Do Not Track"-Direktive</strong>, aber <strong>keinen AdBlocker</strong>. Die "Do Not Track"-Direktive ist gut, aber ein AdBlocker wäre noch besser für deine Privatsphäre.`);
    } else {
      textParts.push(`Wie ich sehe nutzt du <strong>keinen AdBlocker</strong> und <strong>keine "Do Not Track"-Direktive</strong>. Beide könnten deine Privatsphäre verbessern.`);
    }

    // Bot-Erkennung
    if (botInfo) {
      if (botInfo.isBot) {
        textParts.push(`⚠️ Es gibt Hinweise darauf, dass du möglicherweise ein Bot bist: <strong>${botInfo.indicators.join(', ')}</strong>.`);
      } else {
        textParts.push(`Glücklicherweise scheinst du kein Bot zu sein, zumindest deutet nichts von meiner <strong>Bot-Erkennung</strong> darauf hin.`);
      }
    }
    // Closing message
    textParts.push(`Wenn du wissen willst, wie das alles funktioniert, lies einfach weiter. <strong> Viel Spaß beim Entdecken!</strong>`);

    return textParts;
  };

  const detectBrowser = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Opera')) return 'Opera';
    if (ua.includes('Brave')) return 'Brave';
    return 'Unbekannt';
  };

  const getLanguageName = (languageCode) => {
    if (!languageCode) return 'unbekannten';
    
    const langMap = {
      'de': 'deutschen',
      'de-DE': 'deutschen',
      'de-AT': 'deutschen',
      'de-CH': 'deutschen',
      'en': 'englischen',
      'en-US': 'englischen',
      'en-GB': 'englischen',
      'fr': 'französischen',
      'fr-FR': 'französischen',
      'es': 'spanischen',
      'es-ES': 'spanischen',
      'it': 'italienischen',
      'it-IT': 'italienischen',
      'pt': 'portugiesischen',
      'pt-BR': 'portugiesischen',
      'nl': 'niederländischen',
      'nl-NL': 'niederländischen',
      'ru': 'russischen',
      'ru-RU': 'russischen',
      'pl': 'polnischen',
      'pl-PL': 'polnischen',
      'ja': 'japanischen',
      'ja-JP': 'japanischen',
      'zh': 'chinesischen',
      'zh-CN': 'chinesischen',
      'ko': 'koreanischen',
      'ko-KR': 'koreanischen'
    };
    
    if (langMap[languageCode]) {
      return langMap[languageCode];
    }
    
    const langCode = languageCode.split('-')[0].toLowerCase();
    if (langMap[langCode]) {
      return langMap[langCode];
    }
    
    return languageCode;
  };

  const getOSLogo = (platform) => {
    if (!platform) return null;
    const platformLower = platform.toLowerCase();
    
    // Using logo.clearbit.com which is more reliable
    if (platformLower.includes('win')) {
      return 'https://logo.clearbit.com/microsoft.com';
    } else if (platformLower.includes('mac')) {
      return 'https://logo.clearbit.com/apple.com';
    } else if (platformLower.includes('linux')) {
      return 'https://logo.clearbit.com/linuxfoundation.org';
    } else if (platformLower.includes('android')) {
      return 'https://logo.clearbit.com/android.com';
    } else if (platformLower.includes('ios') || platformLower.includes('iphone') || platformLower.includes('ipad')) {
      return 'https://logo.clearbit.com/apple.com';
    }
    return null;
  };

  const getBrowserLogo = (browserName) => {
    if (!browserName) return null;
    const browserLower = browserName.toLowerCase();
    
    // Using logo.clearbit.com which is more reliable
    if (browserLower.includes('chrome') && !browserLower.includes('edge')) {
      return 'https://logo.clearbit.com/chrome.com';
    } else if (browserLower.includes('firefox')) {
      return 'https://logo.clearbit.com/firefox.com';
    } else if (browserLower.includes('edge') || browserLower.includes('edg')) {
      return 'https://logo.clearbit.com/microsoft.com';
    } else if (browserLower.includes('safari')) {
      return 'https://logo.clearbit.com/apple.com';
    } else if (browserLower.includes('opera')) {
      return 'https://logo.clearbit.com/opera.com';
    } else if (browserLower.includes('brave')) {
      return 'https://logo.clearbit.com/brave.com';
    }
    return null;
  };

  // Component for OS Logo with fallback
  const OSLogoImage = ({ platform }) => {
    const [logoError, setLogoError] = useState(false);
    const logoUrl = getOSLogo(platform);
    
    if (!logoUrl || logoError) {
      return <ComputerIcon sx={{ fontSize: 60, opacity: 0.7 }} />;
    }
    
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={platform}
        onError={() => setLogoError(true)}
        sx={{
          width: 60,
          height: 60,
          objectFit: 'contain',
          bgcolor: 'rgba(255,255,255,0.1)',
          borderRadius: 1,
          p: 1
        }}
      />
    );
  };

  // Component for Browser Logo with fallback
  const BrowserLogoImage = ({ browserName }) => {
    const [logoError, setLogoError] = useState(false);
    const logoUrl = getBrowserLogo(browserName);
    
    if (!logoUrl || logoError) {
      return <LanguageIcon sx={{ fontSize: 60, opacity: 0.7 }} />;
    }
    
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={browserName}
        onError={() => setLogoError(true)}
        sx={{
          width: 60,
          height: 60,
          objectFit: 'contain',
          bgcolor: 'rgba(255,255,255,0.1)',
          borderRadius: 1,
          p: 1
        }}
      />
    );
  };

  if (!browserInfo) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Typography>Lade Browser-Informationen...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      {/* Consent Dialog */}
      <Dialog
        open={!consentGiven}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          color: '#ffffff',
          pb: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            <Typography variant="h6" component="div" sx={{ color: '#ffffff' }}>
              Datenschutz-Einwilligung erforderlich
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Wichtiger Hinweis
            </Typography>
          </Alert>
          <DialogContentText>
            <Typography variant="body1" paragraph>
              Um diese Seite zu nutzen, musst du der Datenerfassung zustimmen.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Zweck:</strong> Diese Seite zeigt dir, welche Informationen dein Browser automatisch übermittelt.
              Die Daten werden nur gezeigt, um es zu erklären.
              Sie werden <strong>nicht gespeichert und nicht an andere weitergegeben</strong>.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Was wird erfasst?</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>Deine Browser- und Geräteinformationen</li>
              <li>Deine IP-Adresse und Standortdaten</li>
              <li>Dein Browsing-Verhalten auf der nächsten Seite (Wie weit du scrollst, wie lange du auf einer Seite bist)</li>
              <li>Deine Browser-Fingerprinting-Daten (Canvas, WebGL, Audio)</li>
              <li>Deine installierten Browser-Erweiterungen, sofern erkennbar</li>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Ironie des Datenschutzes:</strong> Um zu sehen, welche Daten über dich 
                gesammelt werden, musst du zunächst der Datensammlung zustimmen. 
                Genau so funktioniert es auch im Internet wenn du eine Website besuchst.
              </Typography>
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Button
            onClick={() => {
              // User lehnt ab - zeige alternative Nachricht
              alert('Sie haben die Datenerfassung abgelehnt. Um zu sehen, welche Daten über Sie gesammelt werden können, ist eine Zustimmung erforderlich.');
            }}
            color="inherit"
            variant="outlined"
          >
            Ablehnen
          </Button>
          <Button
            onClick={() => setConsentGiven(true)}
            variant="contained"
            color="primary"
            sx={{ ml: 1 }}
          >
            Zustimmen und fortfahren
          </Button>
        </DialogActions>
      </Dialog>

      {!consentGiven ? (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ color: '#ffffff' }}>
              Bitte bestätigen Sie die Datenschutz-Einwilligung
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Um die Informationen auf dieser Seite anzuzeigen, müssen Sie zunächst der Datenerfassung zustimmen.
            </Typography>
          </Paper>
        </Container>
      ) : (
        <Box sx={{ 
          background: 'linear-gradient(135deg, #0e0e0e 0%, #1a1a1a 50%, #0e0e0e 100%)',
          minHeight: '100vh', 
          color: '#ffffff',
          position: 'relative',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(66, 165, 245, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          }
        }}>
        {/* Table of Contents - Fixed at top */}
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0,
          right: 0,
          zIndex: 1100, 
          width: '100%',
          background: 'transparent',
          pointerEvents: 'none', // Allow clicks to pass through to content below
        }}>
          <Container maxWidth="lg" sx={{ pointerEvents: 'auto', px: 2 }}>
            <Paper 
              elevation={2} 
              sx={{ 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: { xs: 0, sm: 2 }, // No border radius on mobile for full width
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                overflow: 'visible',
                boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
                backdropFilter: 'blur(10px)',
                width: '100%',
              }}
            >
        <Tabs
          value={tocValue}
          onChange={(e, newValue) => {
            setTocValue(newValue);
            const sections = [
              'welcome',
              'steckbrief',
              'nutzung',
              'risiken',
              'empfehlungen',
              'nerd-stuff'
            ];
            const element = document.getElementById(sections[newValue]);
            if (element) {
              // Temporarily disable scroll detection to avoid conflicts
              isScrollingRef.current = true;
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // Re-enable scroll detection after scroll animation completes
              setTimeout(() => {
                isScrollingRef.current = false;
              }, 1000);
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            overflowX: 'auto', // Enable horizontal scrolling
            '& .MuiTab-root': {
              minWidth: { xs: 100, sm: 120 }, // Smaller on mobile
              textTransform: 'none',
              fontWeight: 500,
              color: '#ffffff',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }, // Responsive font size
              '&.Mui-selected': {
                color: '#42a5f5',
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#2196f3',
            },
            '& .MuiTabs-scrollButtons': {
              color: '#ffffff',
              '&.Mui-disabled': {
                opacity: 0.3,
              }
            }
          }}
        >
          <Tab label="Begrüßung" />
          <Tab label="Digitaler Steckbrief" />
          <Tab label="Nutzung" />
          <Tab label="Risiken" />
          <Tab label="Empfehlungen" />
          <Tab label="Nerd Stuff" />
        </Tabs>
            </Paper>
          </Container>
        </Box>

        {/* Spacer for fixed TOC */}
        <Box sx={{ height: { xs: '56px', sm: '64px' } }} />
        
        <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
          {/* Zurück zum Lagebild Link */}
          <Box sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{
                color: '#42a5f5',
                '&:hover': {
                  backgroundColor: 'rgba(66, 165, 245, 0.1)',
                },
              }}
            >
              Zurück zum Lagebild
            </Button>
          </Box>

      {/* Main Content - Categorized */}
      <Grid container spacing={3}>
        {/* Welcome Section */}
        {browserInfo && (
          <Grid item xs={12} sx={{ mb: 2 }} id="welcome">
            <Paper elevation={3} sx={{ 
              p: 4, 
              background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderLeft: '6px solid #2196f3',
              color: '#ffffff', 
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(10px)',
            }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 700, color: '#ffffff' }}>
                Hallo {user?.first_name || 'User'}!
              </Typography>
              <Box sx={{ 
                fontSize: '1.1em',
                lineHeight: 1.8,
                '& strong': {
                  color: '#ffd700',
                  fontWeight: 600
                }
              }}>
                {generateWelcomeMessage()?.map((text, idx) => (
                  <Typography 
                    key={idx} 
                    variant="body1" 
                    paragraph
                    sx={{ color: 'white', mb: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: text }}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Digitaler Steckbrief */}
        {browserInfo && (
          <>
            <Grid item xs={12} sx={{ mt: 4, mb: 2 }} id="steckbrief">
              <Typography variant="h4" component="h3" sx={{ fontWeight: 700, mb: 1, color: '#ffffff' }}>
                🆔 Dein Digitaler Steckbrief
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Eine Zusammenfassung der wichtigsten Informationen, die dein Browser entweder automatisch sendet, oder einfach mit Hilfe 
                von einem Script auf der Webseite ausgelesen werden können. Oft merkst du davon nicht einmal etwas.
                Das alles passiert im Hintergrund und du hast keine Kontrolle darüber.
              </Typography>
            </Grid>

            {/* Card 1: Dein Standort */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Dein Standort
                    </Typography>
                  </Box>
                  {locationData?.location ? (
                    <>
                      {locationData.location.city && (
                        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, color: '#ffffff' }}>
                          {locationData.location.city}
                        </Typography>
                      )}
                      {locationData.location.state && (
                        <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                          {locationData.location.state}
                        </Typography>
                      )}
                      {locationData.location.country && (
                        <Typography variant="body2" sx={{ mb: 1, color: '#ffffff' }}>
                          <strong>{locationData.location.country}</strong>
                          {locationData.location.countryCode && (
                            <Chip label={locationData.location.countryCode} size="small" sx={{ ml: 1, height: 20 }} />
                          )}
                        </Typography>
                      )}
                      {locationData.ip && (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255, 255, 255, 0.7)' }}>
                          IP: {locationData.ip}
                        </Typography>
                      )}
                    </>
                  ) : locationLoading ? (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Lade Standortdaten...
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Zeitzone: <strong style={{ color: '#ffffff' }}>{browserInfo.timezone}</strong>
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card 2: Dein Gerät */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ComputerIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Dein Gerät
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <OSLogoImage platform={browserInfo.platform} />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#ffffff' }}>
                      {browserInfo.platform}
                    </Typography>
                  </Box>
                  {(() => {
                    const isMobile = browserInfo.touchSupport || 
                                    browserInfo.maxTouchPoints > 0 ||
                                    /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(browserInfo.userAgent || '') ||
                                    (browserInfo.screenWidth <= 1024 && browserInfo.screenHeight <= 1024);
                    return (
                      <Chip 
                        label={isMobile ? 'Mobilgerät' : 'Desktop-PC'} 
                        color={isMobile ? 'secondary' : 'primary'} 
                        size="small" 
                        sx={{ mb: 1 }}
                      />
                    );
                  })()}
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#ffffff' }}>
                    {browserInfo.screenWidth} × {browserInfo.screenHeight} px
                  </Typography>
                  {browserInfo.devicePixelRatio && browserInfo.devicePixelRatio !== 1 && (
                    <Chip label={`${browserInfo.devicePixelRatio}x DPI`} size="small" sx={{ mb: 1 }} />
                  )}
                  {browserInfo.hardwareConcurrency !== 'Nicht verfügbar' && (
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      CPU: {browserInfo.hardwareConcurrency} Kerne
                      {browserInfo.deviceMemory && browserInfo.deviceMemory !== 'Nicht verfügbar' && (
                        <> • RAM: {browserInfo.deviceMemory} GB</>
                      )}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card 3: Dein Browser */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LanguageIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Dein Browser
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <BrowserLogoImage browserName={browserVersion?.browser || detectBrowser()} />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#ffffff' }}>
                      {browserVersion?.browser || detectBrowser()}
                    </Typography>
                  </Box>
                  {browserVersion?.version && browserVersion.version !== 'Unbekannt' && (
                    <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                      Version {browserVersion.version}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Sprache: {browserInfo.language}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 4: Deine Internetverbindung */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <NetworkIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Internetverbindung
                    </Typography>
                  </Box>
                  {connectionInfo ? (
                    <>
                      {connectionInfo.effectiveType && connectionInfo.effectiveType !== 'Nicht verfügbar' && (
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: '#ffffff' }}>
                          {connectionInfo.effectiveType}
                        </Typography>
                      )}
                      {connectionInfo.downlink && connectionInfo.downlink !== 'Nicht verfügbar' && (
                        <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                          Bandbreite: <strong style={{ color: '#ffffff' }}>{connectionInfo.downlink} Mbps</strong>
                        </Typography>
                      )}
                      {connectionInfo.rtt && connectionInfo.rtt !== 'Nicht verfügbar' && (
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Latenz: <strong style={{ color: '#ffffff' }}>{connectionInfo.rtt} ms</strong>
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Verbindungsdaten nicht verfügbar
                    </Typography>
                  )}
                  {locationData?.asn?.org && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                      Provider: {locationData.asn.org}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card 5: Cookies */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <StorageIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Cookies
                    </Typography>
                  </Box>
                  {storageInfo ? (
                    <>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#ffffff' }}>
                        {storageInfo.cookieCount || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                        {storageInfo.cookieCount > 0 ? 'Cookies gesetzt' : 'Keine Cookies'}
                      </Typography>
                      {storageInfo.localStorageAvailable && (
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          LocalStorage: {storageInfo.localStorageKeys} Einträge
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Cookie-Daten nicht verfügbar
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card 6: Navigation */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LanguageIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Navigation
                    </Typography>
                  </Box>
                  {browserInfo.referrer && browserInfo.referrer !== '(kein Referrer)' && browserInfo.referrer !== '' && browserInfo.referrer !== 'Direkter Zugriff (kein Referrer)' ? (
                    <>
                      <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all', color: '#ffffff' }}>
                        Von: <strong style={{ color: '#ffffff' }}>{(() => {
                          try {
                            return new URL(browserInfo.referrer).hostname;
                          } catch {
                            return browserInfo.referrer.substring(0, 30) + '...';
                          }
                        })()}</strong>
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ mb: 1, color: '#ffffff' }}>
                      Referrer: <strong style={{ color: '#ffffff' }}>Direkter Zugriff</strong>
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Host: <strong style={{ color: '#ffffff' }}>{browserInfo.host}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                    Protokoll: <strong style={{ color: '#ffffff' }}>{browserInfo.protocol}</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                    {browserInfo.pathname}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Card 7: Fingerprints */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FingerprintIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Fingerprints
                    </Typography>
                  </Box>
                  {canvasFingerprint && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Canvas</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all', color: '#ffffff' }}>
                        {canvasFingerprint.substring(0, 20)}...
                      </Typography>
                    </Box>
                  )}
                  {audioFingerprint && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Audio</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#ffffff' }}>
                        {audioFingerprint.substring(0, 20)}...
                      </Typography>
                    </Box>
                  )}
                  {fonts && fonts.length > 0 && (
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {fonts.length} Fonts erkannt
                    </Typography>
                  )}
                  {!canvasFingerprint && !audioFingerprint && (!fonts || fonts.length === 0) && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Keine Fingerprints
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card 8: Bot-Erkennung */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <WarningIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Bot-Erkennung
                    </Typography>
                  </Box>
                  {botInfo ? (
                    <>
                      <Chip 
                        label={botInfo.isBot ? 'Bot erkannt' : 'Echter User'} 
                        color={botInfo.isBot ? 'warning' : 'success'} 
                        sx={{ mb: 1 }}
                      />
                      {botInfo.indicators && botInfo.indicators.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          {botInfo.indicators.slice(0, 2).map((indicator, idx) => (
                            <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                              • {indicator}
                            </Typography>
                          ))}
                          {botInfo.indicators.length > 2 && (
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              +{botInfo.indicators.length - 2} weitere
                            </Typography>
                          )}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Prüfung nicht verfügbar
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card 9: VPN */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      VPN
                    </Typography>
                  </Box>
                  {vpnInfo ? (
                    <>
                      <Chip 
                        label={vpnInfo.detected ? 'VPN erkannt' : 'Kein VPN'} 
                        color={vpnInfo.detected ? 'error' : 'success'} 
                        sx={{ mb: 1 }}
                      />
                      {vpnInfo.confidence && (
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                          Konfidenz: {vpnInfo.confidence}
                        </Typography>
                      )}
                      {vpnInfo.indicators && vpnInfo.indicators.length > 0 && (
                        <Box>
                          {vpnInfo.indicators.slice(0, 2).map((indicator, idx) => (
                            <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                              • {indicator}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      VPN-Prüfung nicht verfügbar
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card 10: Extensions */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ExtensionIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Extensions
                    </Typography>
                  </Box>
                  {extensionInfo && extensionInfo.detected && extensionInfo.detected.length > 0 ? (
                    <>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#ffffff' }}>
                        {extensionInfo.detected.length}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {extensionInfo.detected.slice(0, 3).map((ext, idx) => (
                          <Chip key={idx} label={ext} size="small" variant="outlined" />
                        ))}
                        {extensionInfo.detected.length > 3 && (
                          <Chip label={`+${extensionInfo.detected.length - 3}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Keine Extensions erkannt
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card 11: Datenschutz */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Datenschutz
                    </Typography>
                  </Box>
                  
                  {/* HTTPS/TLS */}
                  <Box sx={{ mb: 1.5 }}>
                    {browserInfo.https && browserInfo.secureContext ? (
                      <Chip 
                        label="HTTPS/TLS aktiv" 
                        color="success" 
                        size="small" 
                        sx={{ mb: 0.5 }}
                      />
                    ) : (
                      <Chip 
                        label="Keine Verschlüsselung" 
                        color="error" 
                        size="small" 
                        sx={{ mb: 0.5 }}
                      />
                    )}
                  </Box>

                  {/* Do Not Track */}
                  <Box sx={{ mb: 1.5 }}>
                    {navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes' ? (
                      <Chip 
                        label="Do Not Track aktiv" 
                        color="success" 
                        size="small" 
                        sx={{ mb: 0.5 }}
                      />
                    ) : (
                      <Chip 
                        label="Do Not Track inaktiv" 
                        color="default" 
                        size="small" 
                        sx={{ mb: 0.5 }}
                      />
                    )}
                  </Box>

                  {/* Cookies */}
                  {storageInfo && (
                    <Box sx={{ mb: 1.5 }}>
                      {storageInfo.cookiesEnabled ? (
                        <Chip 
                          label="Cookies aktiviert" 
                          color="warning" 
                          size="small" 
                          sx={{ mb: 0.5 }}
                        />
                      ) : (
                        <Chip 
                          label="Cookies deaktiviert" 
                          color="success" 
                          size="small" 
                          sx={{ mb: 0.5 }}
                        />
                      )}
                    </Box>
                  )}

                  {/* AdBlocker */}
                  {extensionInfo && extensionInfo.detected && extensionInfo.detected.some(ext => ext.includes('AdBlocker')) ? (
                    <Box>
                      <Chip 
                        label="AdBlocker erkannt" 
                        color="success" 
                        size="small"
                      />
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Kein AdBlocker erkannt
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Card 12: Zeit */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={3} sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderLeft: '4px solid #2196f3',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}>
                <CardContent sx={{ color: '#ffffff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTimeIcon sx={{ fontSize: 32, mr: 1, color: '#42a5f5' }} />
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 700, color: '#ffffff' }}>
                      Zeit
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
                    {new Date().toLocaleString('de-DE', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                    Zeitzone: <strong style={{ color: '#ffffff' }}>{browserInfo.timezone}</strong>
                  </Typography>
                  {browserInfo.timezoneOffset && (
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      UTC{browserInfo.timezoneOffset > 0 ? '-' : '+'}{Math.abs(browserInfo.timezoneOffset / 60)} Stunden
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Wofür werden diese Infos genutzt? Section - Radialdiagramm */}
        {browserInfo && (
          <Grid item xs={12} sx={{ mt: 4, mb: 3 }} id="nutzung">
            <Paper 
              elevation={0}
              sx={{ 
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(15, 15, 15, 0.95) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
              }}
            >
              {/* Radialdiagramm: Du in der Mitte */}
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h5" component="h4" sx={{ textAlign: 'center', fontWeight: 700, mb: 3, color: '#ffffff' }}>
                  Wofür werden diese Informationen genutzt?
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', overflow: 'visible' }}>
                  <Box sx={{ position: 'relative', width: 900, height: 900, maxWidth: '100%', background: 'rgba(26, 26, 26, 0.5)', borderRadius: 2, p: 2 }}>
                    <svg width="100%" height="100%" viewBox="0 0 900 900" style={{ overflow: 'visible' }}>
                      <defs>
                        <filter id="glow">
                          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                          <feMerge>
                            <feMergeNode in="coloredBlur"/>
                            <feMergeNode in="SourceGraphic"/>
                          </feMerge>
                        </filter>
                      </defs>
                      
                      {/* Center Circle with Person Icon */}
                      <circle
                        cx="450"
                        cy="450"
                        r="100"
                        fill="rgba(33, 150, 243, 0.2)"
                        stroke="#42a5f5"
                        strokeWidth="3"
                      />
                      
                      {/* Segments */}
                      {(() => {
                        const centerX = 450;
                        const centerY = 450;
                        const radius = 340;
                        const innerRadius = 130;
                        const totalSegments = 13; // 7 nützliche + 6 bedenklich
                        const anglePerSegment = 360 / totalSegments;
                        const segments = [
                          // Nützliche Zwecke (Grün)
                          { 
                            label: 'Seite richtig anzeigen', 
                            description: 'Mobile Version der Webseite für Handy, große Version für den PC. Die Website passt sich automatisch an deine Bildschirmgröße an, damit alles gut lesbar ist.',
                            details: 'Basierend auf deiner Bildschirmauflösung, Gerätetyp und User-Agent wird die optimale Darstellung gewählt.',
                            color: '#4caf50',
                            category: 'nützlich'
                          },
                          { 
                            label: 'Sprache automatisch wählen', 
                            description: 'Wenn dein Browser auf Deutsch eingestellt ist, wird dir die besuchte Webseite in Deutsch angezeigt. Kein manuelles Umschalten nötig.',
                            details: 'Die Website liest deine Browser-Spracheinstellung (Accept-Language Header) und zeigt Inhalte in deiner bevorzugten Sprache.',
                            color: '#4caf50',
                            category: 'nützlich'
                          },
                          { 
                            label: 'Eingeloggt bleiben', 
                            description: 'Durch Cookies weiß die Seite, dass du z.B. schon bei deinem Mailkonto angemeldet bist. Du musst dich nicht jedes Mal neu einloggen.',
                            details: 'Session-Cookies speichern deinen Login-Status, damit du nicht bei jedem Seitenaufruf deine Zugangsdaten eingeben musst.',
                            color: '#4caf50',
                            category: 'nützlich'
                          },
                          { 
                            label: 'Sicherheit', 
                            description: 'Erkennen von ungewöhnlichen Anmeldungen (andere Länder oder Geräte als üblich). Schutz vor Betrug und unberechtigtem Zugriff.',
                            details: 'Webseiten nutzen Standort-, Gerät- und Browser-Informationen, um verdächtige Aktivitäten zu erkennen und dich zu schützen.',
                            color: '#4caf50',
                            category: 'nützlich'
                          },
                          { 
                            label: 'Statistik & Analytics', 
                            description: 'Wie viele Besucher? Welche Geräte? Welche Seiten sind beliebt? Hilft Websites zu verbessern.',
                            details: 'Anonymisierte Statistiken helfen Website-Betreibern zu verstehen, wie ihre Seite genutzt wird und was verbessert werden kann.',
                            color: '#4caf50',
                            category: 'nützlich'
                          },
                          { 
                            label: 'Inhalte deinen Vorlieben anpassen', 
                            description: 'Seite merkt sich z.B.: Dark Mode / Hell-Dunkel-Einstellung, welche Sprache du gewählt hast, welche Startseite/Filter du bevorzugst. Vorteil: Du musst vieles nicht jedes Mal neu einstellen.',
                            details: 'LocalStorage oder Cookies speichern deine Präferenzen (Dark Mode, Sprache, Filter, Layout). Diese Einstellungen bleiben erhalten, auch wenn du die Seite schließt und später wieder öffnest.',
                            color: '#4caf50',
                            category: 'nützlich'
                          },
                          { 
                            label: 'Performance & Technik', 
                            description: 'Webseite schneller laden: Server kann entscheiden, von welchem Standort (Rechenzentrum/CDN) er dir die Seite schickt – oft der, der in deiner Nähe ist. Fehler finden: Aus den technischen Infos (Browser, Version, Gerät) können Entwickler sehen, bei welchem Browser es einen Fehler gibt und ihn gezielt reparieren.',
                            details: 'Basierend auf deiner IP-Adresse wählt der Server das nächstgelegene CDN (Content Delivery Network), um die Ladezeit zu minimieren. Browser- und Geräteinformationen helfen Entwicklern, gerätespezifische Bugs zu identifizieren und zu beheben.',
                            color: '#4caf50',
                            category: 'nützlich'
                          },
                          // Bedenkliche Zwecke (Rot)
                          { 
                            label: 'Tracking über viele Seiten', 
                            description: 'Werbenetzwerke setzen Cookies oder nutzen Fingerprinting, um dich seitenübergreifend wiederzuerkennen. Auch ohne Cookies.',
                            details: 'Durch Browser-Fingerprinting (Canvas, WebGL, Fonts) können Werbenetzwerke dich über verschiedene Websites hinweg verfolgen, selbst wenn du alle Cookies löschst.',
                            color: '#f44336',
                            category: 'bedenklich'
                          },
                          { 
                            label: 'Profile über dich erstellen', 
                            description: 'Interessen (Sport, Politik, Gesundheit), Gewohnheiten (Nachtmensch? Pendler?), besuchte Shops usw. werden gesammelt.',
                            details: 'Datenbroker kombinieren Informationen von vielen Websites, um detaillierte Profile zu erstellen: Alter, Geschlecht, Einkommen, Interessen, Kaufverhalten.',
                            color: '#f44336',
                            category: 'bedenklich'
                          },
                          { 
                            label: 'Personalisierte Werbung', 
                            description: 'Du suchst einmal nach Schuhen – plötzlich siehst du überall Schuh-Werbung. "Retargeting" nennt sich das.',
                            details: 'Werbung wird basierend auf deinem Browsing-Verhalten, Standort, Gerät und Profil angepasst. Du siehst gezielt Produkte, die zu dir passen sollen.',
                            color: '#f44336',
                            category: 'bedenklich'
                          },
                          { 
                            label: 'Angepasste Inhalte', 
                            description: 'Nicht nur Werbung, auch Inhalte werden passend zu deinen Vorlieben angepasst. Was dir angezeigt wird, unterscheidet sich eventuell von den Dingen, die andere angezeigt bekommen.',
                            details: 'Social Media, News-Seiten und Streaming-Dienste passen Inhalte an dein Profil an. Das kann zu "Filterblasen" führen, wo du nur noch bestimmte Inhalte oder Meinungen siehst.',
                            color: '#f44336',
                            category: 'bedenklich'
                          },
                          { 
                            label: 'Daten verkaufen oder teilen', 
                            description: 'Viele Firmen verkaufen oder teilen gesammelte Daten mit anderen Firmen: Werbeunternehmen, Datenbroker (deren Geschäftsmodell nur Datenhandel ist). Du weißt oft nicht, wer am Ende alles über dich Bescheid weiß.',
                            details: 'Deine gesammelten Daten werden an Dritte verkauft oder geteilt. Datenbroker sammeln Informationen von vielen Quellen und erstellen detaillierte Profile, die sie an Werbeunternehmen, Versicherungen, Arbeitgeber oder andere Interessenten verkaufen.',
                            color: '#f44336',
                            category: 'bedenklich'
                          },
                          { 
                            label: 'Geoblocking & Zensur', 
                            description: 'Über deine IP-Adresse erkennt man dein Land. Damit kann man: Inhalte sperren ("In deinem Land nicht verfügbar"), bestimmte Themen blockieren oder filtern. Teilweise aus rechtlichen Gründen, teilweise zur Zensur.',
                            details: 'Basierend auf deiner IP-Adresse wird dein geografischer Standort ermittelt. Websites, Streaming-Dienste und Regierungen können daraufhin Inhalte blockieren oder filtern. Dies kann aus Lizenzgründen (z.B. Netflix), rechtlichen Gründen (z.B. Pornografie) oder zur Zensur (z.B. China) geschehen.',
                            color: '#f44336',
                            category: 'bedenklich'
                          },
                        ].map((segment, index) => ({
                          ...segment,
                          startAngle: index * anglePerSegment,
                          angle: anglePerSegment
                        }));

                        return segments.map((segment, index) => {
                          const startAngleRad = ((segment.startAngle - 90) * Math.PI) / 180;
                          const endAngleRad = ((segment.startAngle + segment.angle - 90) * Math.PI) / 180;
                          
                          const x1 = centerX + innerRadius * Math.cos(startAngleRad);
                          const y1 = centerY + innerRadius * Math.sin(startAngleRad);
                          const x2 = centerX + radius * Math.cos(startAngleRad);
                          const y2 = centerY + radius * Math.sin(startAngleRad);
                          const x3 = centerX + radius * Math.cos(endAngleRad);
                          const y3 = centerY + radius * Math.sin(endAngleRad);
                          const x4 = centerX + innerRadius * Math.cos(endAngleRad);
                          const y4 = centerY + innerRadius * Math.sin(endAngleRad);
                          
                          const largeArc = segment.angle > 180 ? 1 : 0;
                          
                          const pathData = [
                            `M ${x1} ${y1}`,
                            `L ${x2} ${y2}`,
                            `A ${radius} ${radius} 0 ${largeArc} 1 ${x3} ${y3}`,
                            `L ${x4} ${y4}`,
                            `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1} ${y1}`,
                            'Z'
                          ].join(' ');
                          
                          // Label position
                          const labelAngle = ((segment.startAngle + segment.angle / 2 - 90) * Math.PI) / 180;
                          const labelRadius = (innerRadius + radius) / 2;
                          const labelX = centerX + labelRadius * Math.cos(labelAngle);
                          const labelY = centerY + labelRadius * Math.sin(labelAngle);
                          
                          // Split label into multiple lines if needed
                          const words = segment.label.split(' ');
                          const lines = [];
                          let currentLine = '';
                          words.forEach((word, i) => {
                            if ((currentLine + word).length > 12 && currentLine) {
                              lines.push(currentLine.trim());
                              currentLine = word + ' ';
                            } else {
                              currentLine += word + ' ';
                            }
                            if (i === words.length - 1) {
                              lines.push(currentLine.trim());
                            }
                          });
                          
                          return (
                            <g key={index}>
                              <path
                                d={pathData}
                                fill={segment.color}
                                fillOpacity="0.7"
                                stroke={segment.color}
                                strokeWidth="2"
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={(e) => {
                                  e.target.setAttribute('fillOpacity', '0.9');
                                  e.target.setAttribute('filter', 'url(#glow)');
                                }}
                                onMouseLeave={(e) => {
                                  e.target.setAttribute('fillOpacity', '0.7');
                                  e.target.setAttribute('filter', 'none');
                                }}
                                onClick={() => {
                                  setDetailDialog({
                                    title: segment.label,
                                    description: segment.description,
                                    details: segment.details,
                                    color: segment.color,
                                    category: segment.category
                                  });
                                }}
                              />
                              <text
                                x={labelX}
                                y={labelY - (lines.length - 1) * 9}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="#ffffff"
                                fontSize="16"
                                fontWeight="700"
                                style={{ pointerEvents: 'none', userSelect: 'none', textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}
                              >
                                {lines.map((line, lineIndex) => (
                                  <tspan
                                    key={lineIndex}
                                    x={labelX}
                                    dy={lineIndex === 0 ? 0 : 20}
                                    textAnchor="middle"
                                  >
                                    {line}
                                  </tspan>
                                ))}
                              </text>
                            </g>
                          );
                        });
                      })()}
                    </svg>
                    
                    {/* Person Icon in Center */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 60, color: '#42a5f5' }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, mt: 1, color: '#42a5f5' }}>
                        Du
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ mb: 2, color: '#ffffff' }}>
                    Klicke auf ein Segment für Details
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, bgcolor: '#4caf50', borderRadius: '50%' }} />
                      <Typography variant="caption" sx={{ color: '#ffffff' }}>Nützliche Zwecke</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, bgcolor: '#ff5722', borderRadius: '50%' }} />
                      <Typography variant="caption" sx={{ color: '#ffffff' }}>Bedenkliche Zwecke</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Detail Dialog für Radialdiagramm */}
        <Dialog
          open={detailDialog !== null && detailDialog?.category !== undefined}
          onClose={() => setDetailDialog(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            }
          }}
        >
          {detailDialog && detailDialog.category && (
            <>
              <DialogTitle 
                sx={{ 
                  bgcolor: detailDialog.category === 'nützlich' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                  borderLeft: `4px solid ${detailDialog.color}`,
                  pb: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: detailDialog.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}
                  >
                    {detailDialog.category === 'nützlich' ? '✅' : '⚠️'}
                  </Box>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
                    {detailDialog.title}
                  </Typography>
                </Box>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                <Typography variant="body1" paragraph>
                  {detailDialog.description}
                </Typography>
                
                {detailDialog.details && (
                  <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Wie funktioniert das genau?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {detailDialog.details}
                    </Typography>
                  </Box>
                )}
                
                <Alert 
                  severity={detailDialog.category === 'nützlich' ? 'success' : 'warning'} 
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2">
                    {detailDialog.category === 'nützlich' 
                      ? '✅ Diese Nutzung ist in der Regel unbedenklich und dient der Funktionalität der Website. Sie ist technisch notwendig oder verbessert deine Nutzererfahrung.'
                      : '⚠️ Diese Nutzung kann deine Privatsphäre beeinträchtigen und wird oft ohne dein explizites Wissen durchgeführt. Du kannst sie durch Browser-Einstellungen oder Erweiterungen einschränken.'}
                  </Typography>
                </Alert>
              </DialogContent>
              <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Button onClick={() => setDetailDialog(null)} variant="contained" color="primary">
                  Schließen
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Risiko-Sektion */}
        {browserInfo && (
          <>
            {/* Top-Risiken Hero-Sektion */}
            <Grid item xs={12} sx={{ mt: 4, mb: 3 }} id="risiken">
              <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 700, mb: 1, color: '#ffffff' }}>
                Die wichtigsten Risiken auf einen Blick
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: '#ffffff' }}>
                Klicke auf eine Kachel für Details
              </Typography>
              
              <Grid container spacing={3}>
                {/* Kachel 1: Wiedererkennung & Profiling */}
                <Grid item xs={12} md={4}>
                  <Card
                    elevation={topRiskOpen === 'wiedererkennung' ? 8 : 3}
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      transform: topRiskOpen === 'wiedererkennung' ? 'scale(1.02)' : 'scale(1)',
                      bgcolor: topRiskOpen === 'wiedererkennung' ? 'error.light' : 'error.main',
                      color: 'error.contrastText',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 8,
                      }
                    }}
                    onClick={() => {
                      setTopRiskOpen(topRiskOpen === 'wiedererkennung' ? null : 'wiedererkennung');
                      setViewedRisks(prev => new Set([...prev, 'wiedererkennung']));
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography 
                          variant="h2" 
                          sx={{ 
                            mr: 2,
                            animation: topRiskOpen === 'wiedererkennung' ? 'pulse 2s infinite' : 'none',
                            '@keyframes pulse': {
                              '0%, 100%': { transform: 'scale(1)' },
                              '50%': { transform: 'scale(1.1)' }
                            }
                          }}
                        >
                          👁️
                        </Typography>
                        <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                          Wiedererkennung & Profiling
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ opacity: 0.95, fontSize: '1.1rem' }}>
                        Websites können dich wiedererkennen – auch ohne Cookies.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Kachel 2: Gezielte Angriffe */}
                <Grid item xs={12} md={4}>
                  <Card
                    elevation={topRiskOpen === 'angriffe' ? 8 : 3}
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      transform: topRiskOpen === 'angriffe' ? 'scale(1.02)' : 'scale(1)',
                      bgcolor: topRiskOpen === 'angriffe' ? 'warning.light' : 'warning.main',
                      color: 'warning.contrastText',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 8,
                      }
                    }}
                    onClick={() => {
                      setTopRiskOpen(topRiskOpen === 'angriffe' ? null : 'angriffe');
                      setViewedRisks(prev => new Set([...prev, 'angriffe']));
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography 
                          variant="h2" 
                          sx={{ 
                            mr: 2,
                            animation: topRiskOpen === 'angriffe' ? 'shake 0.5s infinite' : 'none',
                            '@keyframes shake': {
                              '0%, 100%': { transform: 'rotate(0deg)' },
                              '25%': { transform: 'rotate(-5deg)' },
                              '75%': { transform: 'rotate(5deg)' }
                            }
                          }}
                        >
                          💣
                        </Typography>
                        <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                          Gezielte Angriffe
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ opacity: 0.95, fontSize: '1.1rem' }}>
                        Alte Software-Versionen machen dich angreifbarer.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Kachel 3: Datenschutz & Recht */}
                <Grid item xs={12} md={4}>
                  <Card
                    elevation={topRiskOpen === 'datenschutz' ? 8 : 3}
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      transform: topRiskOpen === 'datenschutz' ? 'scale(1.02)' : 'scale(1)',
                      bgcolor: topRiskOpen === 'datenschutz' ? 'info.light' : 'info.main',
                      color: 'info.contrastText',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 8,
                      }
                    }}
                    onClick={() => {
                      setTopRiskOpen(topRiskOpen === 'datenschutz' ? null : 'datenschutz');
                      setViewedRisks(prev => new Set([...prev, 'datenschutz']));
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography 
                          variant="h2" 
                          sx={{ 
                            mr: 2,
                            animation: topRiskOpen === 'datenschutz' ? 'pulse 2s infinite' : 'none',
                            '@keyframes pulse': {
                              '0%, 100%': { transform: 'scale(1)' },
                              '50%': { transform: 'scale(1.1)' }
                            }
                          }}
                        >
                          ⚖️
                        </Typography>
                        <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                          Datenschutz & Recht
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ opacity: 0.95, fontSize: '1.1rem' }}>
                        Fingerprinting kann als personenbezogene Daten gelten (DSGVO).
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Detail-Panel für Top-Risiken */}
              {topRiskOpen && (
                <Box sx={{ mt: 3 }}>
                  <Paper 
                    elevation={4}
                    sx={{ 
                      p: 4,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '2px solid',
                      borderColor: topRiskOpen === 'wiedererkennung' ? 'error.main' : 
                                   topRiskOpen === 'angriffe' ? 'warning.main' : 'info.main'
                    }}
                  >
                    {topRiskOpen === 'wiedererkennung' && (
                      <>
                        <Typography variant="h5" component="h4" sx={{ fontWeight: 700, mb: 2 }}>
                          👁️ Wiedererkennung & Profiling
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          <strong>Dein Fingerprint ist wie ein "digitaler Ausweis".</strong>
                        </Typography>
                        <Typography variant="body1" paragraph>
                          Er erlaubt es, dein Verhalten über viele Websites hinweg zu verknüpfen.
                        </Typography>
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Was heißt das für dich?
                          </Typography>
                          <Typography variant="body1">
                            Werbung und Inhalte können dir sehr gezielt folgen – auch wenn du Cookies blockierst.
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Anti-Fingerprinting-Browser verwenden (z.B. Tor Browser, Firefox mit Privacy-Einstellungen), 
                            Browser-Erweiterungen nutzen (z.B. CanvasBlocker, uBlock Origin), JavaScript selektiv blockieren, 
                            regelmäßig Browser-Daten löschen, verschiedene Browser für verschiedene Aktivitäten nutzen.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, wiedererkennung: !prev.wiedererkennung }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.wiedererkennung ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.wiedererkennung && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, du gehst in ein Geschäft und der Verkäufer erkennt dich sofort wieder – 
                              auch wenn du eine Maske trägst. Er weiß genau, was du letztes Mal gekauft hast und zeigt dir 
                              sofort ähnliche Produkte. Genauso funktioniert Browser-Fingerprinting: Websites erkennen dich 
                              wieder, auch ohne Cookies, und passen Inhalte und Werbung an dein Profil an."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                    
                    {topRiskOpen === 'angriffe' && (
                      <>
                        <Typography variant="h5" component="h4" sx={{ fontWeight: 700, mb: 2 }}>
                          💣 Gezielte Angriffe
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          <strong>Alte Browser- und Systemversionen haben bekannte Sicherheitslücken.</strong>
                        </Typography>
                        <Typography variant="body1" paragraph>
                          Angreifer können aus deinem User-Agent erkennen, welche Version du nutzt, und gezielt Schwachstellen ausnutzen.
                        </Typography>
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Was heißt das für dich?
                          </Typography>
                          <Typography variant="body1">
                            Du bist anfälliger für Malware, Phishing und andere Cyberangriffe. Regelmäßige Updates sind essentiell.
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Regelmäßig Browser und System aktualisieren, automatische Updates aktivieren, 
                            Sicherheitsupdates sofort installieren, Virenscanner und Firewall nutzen, 
                            verdächtige Links und E-Mails vermeiden, starke Passwörter verwenden.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, angriffe: !prev.angriffe }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.angriffe ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.angriffe && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, du hast ein altes Auto mit einem bekannten Sicherheitsproblem. 
                              Ein Dieb sieht dein Auto und weiß genau, wie er es aufbrechen kann, weil er die Schwachstelle kennt. 
                              Genauso funktionieren gezielte Cyberangriffe: Angreifer sehen aus deinem Browser-Fingerprint, 
                              welche alte Version du nutzt, und nutzen bekannte Sicherheitslücken gezielt aus."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                    
                    {topRiskOpen === 'datenschutz' && (
                      <>
                        <Typography variant="h5" component="h4" sx={{ fontWeight: 700, mb: 2 }}>
                          ⚖️ Datenschutz & Recht
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          <strong>Browser-Fingerprinting kann als personenbezogenes Datum gelten.</strong>
                        </Typography>
                        <Typography variant="body1" paragraph>
                          Laut DSGVO müssen Websites eine Rechtsgrundlage für die Verarbeitung haben und dich informieren.
                        </Typography>
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Was heißt das für dich?
                          </Typography>
                          <Typography variant="body1">
                            Viele Websites sammeln diese Daten ohne deine explizite Einwilligung – das kann rechtswidrig sein.
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Datenschutzerklärungen lesen, Cookie-Banner genau prüfen und nur notwendige Cookies akzeptieren, 
                            Auskunftsrechte nach DSGVO nutzen, Beschwerden bei Datenschutzbehörden einreichen, 
                            Browser-Einstellungen für Datenschutz optimieren, Privacy-Erweiterungen verwenden.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, datenschutz: !prev.datenschutz }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.datenschutz ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.datenschutz && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, jemand sammelt heimlich Informationen über dich – wie du aussiehst, 
                              wo du wohnst, was du magst – ohne dass du es weißt oder zustimmst. Das wäre in der 
                              realen Welt illegal. Genauso ist es mit Browser-Fingerprinting: Viele Websites sammeln 
                              diese Daten ohne deine explizite Einwilligung, obwohl sie laut DSGVO eigentlich deine 
                              Zustimmung brauchen würden."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                </Box>
              )}
            </Grid>

            {/* Weitere Risiken */}
            <Grid item xs={12} sx={{ mt: 4, mb: 2 }}>
              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 700 }}>
                Weitere Risiken für dich
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
                Über Tracking hinaus: Was Standard-User real betrifft.
              </Typography>

              {/* Tabs/Chips */}
              <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[
                  { id: 'preise', label: '💰 Preise', icon: '💰' },
                  { id: 'profiling', label: '🧩 Profiling', icon: '🧩' },
                  { id: 'reident', label: '🕵️‍♀️ Re-Identifizierung', icon: '🕵️‍♀️' },
                  { id: 'sperren', label: '🚧 Sperren', icon: '🚧' },
                  { id: 'anonymisierung', label: '🛠️ Anonymisierung', icon: '🛠️' },
                  { id: 'intransparenz', label: '🔍 Intransparenz', icon: '🔍' },
                  { id: 'datenlecks', label: '💧 Datenlecks', icon: '💧' },
                ].map((tab) => (
                  <Chip
                    key={tab.id}
                    label={tab.label}
                    onClick={() => {
                      setAdditionalRiskTab(tab.id);
                      setViewedRisks(prev => new Set([...prev, tab.id]));
                    }}
                    color={additionalRiskTab === tab.id ? 'primary' : 'default'}
                    variant={additionalRiskTab === tab.id ? 'filled' : 'outlined'}
                    sx={{
                      fontSize: '0.95rem',
                      py: 2.5,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      color: '#ffffff',
                      borderColor: additionalRiskTab === tab.id ? 'primary.main' : 'rgba(255, 255, 255, 0.5)',
                      '& .MuiChip-label': {
                        color: '#ffffff',
                      },
                      '&:hover': {
                        transform: 'scale(1.05)',
                        borderColor: 'rgba(255, 255, 255, 0.8)',
                      }
                    }}
                  />
                ))}
              </Box>

              {/* Fortschrittsindikator */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {viewedRisks.size} / 10 Risiken angesehen
                </Typography>
                <Box sx={{ mt: 1, height: 4, bgcolor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
                  <Box 
                    sx={{ 
                      height: '100%', 
                      bgcolor: 'primary.main',
                      width: `${(viewedRisks.size / 10) * 100}%`,
                      transition: 'width 0.3s ease'
                    }} 
                  />
                </Box>
              </Box>

              {/* Detail-Karte für aktiven Tab */}
              <Box sx={{ minHeight: 300 }}>
                <Fade in={true} timeout={300}>
                  <Card elevation={4} sx={{ p: 3 }}>
                    {additionalRiskTab === 'preise' && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h2" sx={{ mr: 2 }}>💰</Typography>
                          <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                            Preisdiskriminierung
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                            {[1, 2, 3].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: i <= 2 ? 'warning.main' : 'grey.300'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          Preise können sich nach Gerät, Standort oder Nutzungsverhalten richten.
                        </Typography>
                        
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🔍 Was bedeutet das für dich?
                          </Typography>
                          <Typography variant="body1">
                            Du kannst für Flug, Hotel oder Shop mehr zahlen als andere – ohne es zu merken.
                          </Typography>
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Preisvergleiche nutzen, im privaten Modus suchen, wenn möglich mehrere Geräte/Browser testen.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, preise: !prev.preise }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.preise ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.preise && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, du gehst in einen Laden. Der Verkäufer sieht, dass du teure Markenkleidung trägst und ein neues Smartphone hast. 
                              Er zeigt dir automatisch die teureren Produkte – auch wenn die günstigeren genauso gut sind. 
                              Genauso funktioniert Preisdiskriminierung online: Websites sehen dein Gerät und Standort und passen die Preise an."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    {additionalRiskTab === 'profiling' && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h2" sx={{ mr: 2 }}>🧩</Typography>
                          <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                            Profiling & Filterblasen
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                            {[1, 2, 3].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: i <= 3 ? 'error.main' : 'grey.300'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          Deine Interessen, Gewohnheiten und Verhalten werden analysiert und in Profile umgewandelt.
                        </Typography>
                        
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🔍 Was bedeutet das für dich?
                          </Typography>
                          <Typography variant="body1">
                            Du siehst nur noch Inhalte, die zu deinem Profil passen – das kann zu "Filterblasen" führen, wo du nur noch bestimmte Meinungen siehst.
                          </Typography>
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Verschiedene Quellen nutzen, regelmäßig Cookies löschen, Browser-Einstellungen anpassen, alternative Suchmaschinen verwenden.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, profiling: !prev.profiling }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.profiling ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.profiling && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, du liest nur noch eine Zeitung, die genau deine Meinung widerspiegelt. 
                              Du denkst, das ist die ganze Welt – aber in Wirklichkeit siehst du nur einen kleinen Ausschnitt. 
                              So funktionieren Filterblasen: Algorithmen zeigen dir nur noch, was zu deinem Profil passt."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    {additionalRiskTab === 'reident' && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h2" sx={{ mr: 2 }}>🕵️‍♀️</Typography>
                          <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                            Re-Identifizierung
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                            {[1, 2, 3].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: i <= 2 ? 'warning.main' : 'grey.300'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          Selbst anonymisierte Daten können durch Kombination verschiedener Informationen wieder zugeordnet werden.
                        </Typography>
                        
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🔍 Was bedeutet das für dich?
                          </Typography>
                          <Typography variant="body1">
                            Auch wenn Daten "anonymisiert" verkauft werden, können Datenbroker dich durch Kombination verschiedener Quellen identifizieren.
                          </Typography>
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Minimale Datenfreigabe, verschiedene Browser/Profile für verschiedene Aktivitäten nutzen, VPN verwenden.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, reident: !prev.reident }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.reident ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.reident && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, du trägst eine Maske, aber jemand erkennt dich trotzdem an deiner Kleidung, 
                              deiner Größe und deinem Gang. So funktioniert Re-Identifizierung: 
                              Auch anonymisierte Daten können durch Kombination verschiedener Informationen wieder zugeordnet werden."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    {additionalRiskTab === 'sperren' && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h2" sx={{ mr: 2 }}>🚧</Typography>
                          <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                            Fehlalarme & Sperren
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                            {[1, 2, 3].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: i <= 1 ? 'info.main' : 'grey.300'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          Ungewöhnliche Browser-Konfigurationen oder VPN-Nutzung können zu Fehlalarmen und Sperren führen.
                        </Typography>
                        
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🔍 Was bedeutet das für dich?
                          </Typography>
                          <Typography variant="body1">
                            Du könntest fälschlicherweise als Bot oder Betrüger eingestuft werden und von Websites gesperrt werden.
                          </Typography>
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Normale Browser-Einstellungen verwenden, bei Problemen Support kontaktieren, vertrauenswürdige VPNs nutzen.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, sperren: !prev.sperren }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.sperren ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.sperren && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, du gehst in einen Laden mit einer ungewöhnlichen Maske. 
                              Der Sicherheitsdienst hält dich für verdächtig und lässt dich nicht rein – obwohl du nichts falsch gemacht hast. 
                              So können ungewöhnliche Browser-Konfigurationen zu Fehlalarmen führen."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    {additionalRiskTab === 'anonymisierung' && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h2" sx={{ mr: 2 }}>🛠️</Typography>
                          <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                            Umgehung von Anonymisierungs-Tools
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                            {[1, 2, 3].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: i <= 2 ? 'warning.main' : 'grey.300'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          Moderne Fingerprinting-Techniken können auch Anonymisierungs-Tools wie VPNs oder Tor umgehen.
                        </Typography>
                        
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🔍 Was bedeutet das für dich?
                          </Typography>
                          <Typography variant="body1">
                            Selbst mit VPN oder Tor kannst du durch Browser-Fingerprinting wiedererkannt werden.
                          </Typography>
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Anti-Fingerprinting-Browser verwenden (z.B. Tor Browser), JavaScript blockieren, Canvas-Fingerprinting blockieren.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, anonymisierung: !prev.anonymisierung }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.anonymisierung ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.anonymisierung && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, du trägst eine Tarnkappe, aber jemand erkennt dich trotzdem an deiner Stimme, 
                              deinem Gang und deinen Gewohnheiten. So funktioniert Browser-Fingerprinting: 
                              Auch mit VPN oder Tor kannst du durch die Kombination vieler kleiner Details wiedererkannt werden."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    {additionalRiskTab === 'intransparenz' && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h2" sx={{ mr: 2 }}>🔍</Typography>
                          <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                            Intransparenz & fehlende Kontrolle
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                            {[1, 2, 3].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: i <= 3 ? 'error.main' : 'grey.300'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          Browser-Fingerprinting passiert unsichtbar im Hintergrund, oft ohne explizite Einwilligung oder verständliche Hinweise.
                        </Typography>
                        
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🔍 Was bedeutet das für dich?
                          </Typography>
                          <Typography variant="body1" paragraph>
                            Selbst wenn du theoretisch nach DSGVO Rechte hast (Auskunft, Löschung), ist praktisch oft unklar, wer welche Daten sammelt und wo sie verarbeitet oder gespeichert werden.
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 2 }}>
                            Für Nutzer ohne technische Expertise oder Detailwissen ist es faktisch kaum möglich, diese Mechanismen vollständig zu überblicken oder gezielt abzustellen.
                          </Typography>
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Datenschutz-Einstellungen im Browser aktivieren, Privacy-Erweiterungen nutzen (z.B. Privacy Badger, uBlock Origin), 
                            regelmäßig Cookies und Browser-Daten löschen, Datenschutzerklärungen lesen und Auskunftsrechte nutzen.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, intransparenz: !prev.intransparenz }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.intransparenz ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.intransparenz && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, du gehst durch eine Stadt und überall werden heimlich Fotos von dir gemacht – 
                              aber du siehst keine Kameras, bekommst keine Hinweise und weißt nicht, wer die Fotos macht oder wo sie gespeichert werden. 
                              So funktioniert Browser-Fingerprinting: Es passiert unsichtbar im Hintergrund, 
                              und selbst wenn du theoretisch Rechte hast, weißt du oft nicht, wo du diese einfordern sollst."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    {additionalRiskTab === 'datenlecks' && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h2" sx={{ mr: 2 }}>💧</Typography>
                          <Typography variant="h5" component="h4" sx={{ fontWeight: 700 }}>
                            Datenlecks
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                            {[1, 2, 3].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: i <= 3 ? 'error.main' : 'grey.300'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                          Gesammelte Browser-Daten und Fingerprints werden oft in großen Datenbanken gespeichert, die Ziel von Cyberangriffen werden können.
                        </Typography>
                        
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🔍 Was bedeutet das für dich?
                          </Typography>
                          <Typography variant="body1" paragraph>
                            Wenn Unternehmen, die deine Browser-Daten sammeln, gehackt werden, können deine Fingerprints und Profile in falsche Hände geraten.
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 2 }}>
                            Je mehr Daten über dich gesammelt werden, desto größer ist das Risiko bei einem Datenleck.
                          </Typography>
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            🛡️ Was kannst du tun?
                          </Typography>
                          <Typography variant="body1">
                            Minimale Datenfreigabe, regelmäßig Passwörter ändern, Zwei-Faktor-Authentifizierung nutzen, 
                            Datenlecks überprüfen (z.B. haveibeenpwned.com), verschiedene Passwörter für verschiedene Dienste verwenden.
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          onClick={() => setPlainLanguageToggle(prev => ({ ...prev, datenlecks: !prev.datenlecks }))}
                          sx={{ mt: 2 }}
                        >
                          {plainLanguageToggle.datenlecks ? '❌' : '❓'} Beispiel aus dem Alltag
                        </Button>
                        {plainLanguageToggle.datenlecks && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "Stell dir vor, du gibst einem Unternehmen eine Kopie deines Ausweises zur Aufbewahrung. 
                              Eines Tages wird das Unternehmen ausgeraubt und deine Ausweiskopie wird gestohlen. 
                              So funktionieren Datenlecks: Unternehmen sammeln deine Browser-Daten, 
                              und wenn sie gehackt werden, können diese Daten in falsche Hände geraten – 
                              auch wenn du selbst nichts falsch gemacht hast."
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Card>
                </Fade>
              </Box>
            </Grid>

            {/* Merksatz Banner */}
            <Grid item xs={12} sx={{ mt: 4, mb: 2 }}>
              <Paper
                elevation={6}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  position: 'sticky',
                  bottom: 20,
                  zIndex: 10,
                  minHeight: '140px', // Feste Mindesthöhe, um Layout-Verschiebungen zu vermeiden
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  animation: 'pulseBanner 3s infinite',
                  '@keyframes pulseBanner': {
                    '0%, 100%': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
                    '50%': { boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h4" sx={{ mr: 1 }}>💡</Typography>
                  <Typography variant="h6" component="h4" sx={{ fontWeight: 700 }}>
                    Merksatz
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                  Nicht nur "Werbung folgt dir" – auch{' '}
                  <AnimatedWord word="Preise" delay={0} />
                  ,{' '}
                  <AnimatedWord word="Inhalte" delay={2000} />
                  {' '}und{' '}
                  <AnimatedWord word="Angriffe" delay={4000} />
                  {' '}können sich an deinen Fingerprint anpassen.
                </Typography>
              </Paper>
            </Grid>

            {/* Grundätzliche Empfehlungen im Vergleich */}
            <Grid item xs={12} sx={{ mt: 4, mb: 2 }} id="empfehlungen">
              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
                Grundätzliche Empfehlungen im Vergleich
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                Diese Übersicht zeigt, was die einzelnen Maßnahmen bringen, wie aufwendig sie sind und für wen sie sich eignen.
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  💡 <strong>Tipp:</strong> Starte mit <strong>uBlock Origin / Privacy Badger</strong> und ergänze bei Bedarf weitere Maßnahmen.
                </Typography>
              </Alert>

              <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Maßnahme</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Was sie bringt</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Aufwand</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Für wen?</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* 1 Stern */}
                    <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>uBlock Origin / Privacy Badger</TableCell>
                      <TableCell>Blockiert Werbung und viele versteckte Tracking-Skripte automatisch.</TableCell>
                      <TableCell>
                        ⭐ <Typography component="small" variant="body2" color="text.secondary">nur Erweiterung installieren</Typography>
                      </TableCell>
                      <TableCell>Alle Nutzer – idealer Startpunkt</TableCell>
                    </TableRow>
                    {/* 2 Sterne */}
                    <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>VPN verwenden</TableCell>
                      <TableCell>Verschleiert deine IP-Adresse und deinen groben Standort.</TableCell>
                      <TableCell>
                        ⭐⭐ <Typography component="small" variant="body2" color="text.secondary">Dienst auswählen, App nutzen</Typography>
                      </TableCell>
                      <TableCell>Alle, die oft unterwegs oder in öffentlichem WLAN sind</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>Firefox „Resist Fingerprinting"</TableCell>
                      <TableCell>Erschwert Fingerprinting, indem Browser-Merkmale vereinheitlicht werden.</TableCell>
                      <TableCell>
                        ⭐⭐ <Typography component="small" variant="body2" color="text.secondary">about:config anpassen</Typography>
                      </TableCell>
                      <TableCell>Privatsphäre-bewusste Firefox-Nutzer</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>CanvasBlocker</TableCell>
                      <TableCell>Schützt vor Canvas-Fingerprinting durch Blockieren/Verfälschen von Canvas-Abfragen.</TableCell>
                      <TableCell>
                        ⭐⭐ <Typography component="small" variant="body2" color="text.secondary">Add-on installieren, ggf. Feintuning</Typography>
                      </TableCell>
                      <TableCell>Fortgeschrittene Nutzer mit Fokus auf Fingerprinting</TableCell>
                    </TableRow>
                    {/* 3 Sterne */}
                    <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>Tor Browser</TableCell>
                      <TableCell>Hohe Anonymität, blockiert viele Tracking- und Fingerprinting-Techniken.</TableCell>
                      <TableCell>
                        ⭐⭐⭐ <Typography component="small" variant="body2" color="text.secondary">eigener Browser, langsamer</Typography>
                      </TableCell>
                      <TableCell>Nutzer mit hohen Anforderungen an Anonymität</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>WebRTC deaktivieren</TableCell>
                      <TableCell>Reduziert IP-Leaks, die trotz VPN auftreten können.</TableCell>
                      <TableCell>
                        ⭐⭐⭐ <Typography component="small" variant="body2" color="text.secondary">Einstellungen/Add-on</Typography>
                      </TableCell>
                      <TableCell>VPN-Nutzer, die IP-Leaks verhindern wollen</TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>JavaScript selektiv deaktivieren</TableCell>
                      <TableCell>Stoppt viele Tracking-Skripte, gibt dir sehr viel Kontrolle.</TableCell>
                      <TableCell>
                        ⭐⭐⭐ <Typography component="small" variant="body2" color="text.secondary">viel Konfiguration nötig</Typography>
                      </TableCell>
                      <TableCell>Sehr technikaffine Power-User mit viel Geduld</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </>
        )}

        {/* Nerd Stuff Section */}
        <Grid item xs={12} sx={{ mt: 4, mb: 2 }} id="nerd-stuff">
          <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Nerd Stuff
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
            Detaillierte technische Informationen für Interessierte
          </Typography>
        </Grid>

        {/* 1. Technische Verbindungsdaten */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">1. Technische Verbindungsdaten</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ color: '#ffffff' }}>
              <InfoCard 
            title="1. Technische Verbindungsdaten" 
            icon={NetworkIcon} 
            color="primary"
          >
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Diese werden automatisch beim Aufruf jeder Website übertragen (HTTP/HTTPS-Protokoll)
              </Typography>
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <List dense>
                  <InfoRow label="User-Agent (Browsertyp & Version)" value={browserInfo.userAgent} />
                  <InfoRow label="Betriebssystem" value={browserInfo.platform} />
                  <InfoRow label="Browser-Hersteller" value={browserInfo.vendor} />
                  <InfoRow label="Spracheinstellungen (Accept-Language)" value={browserInfo.languages.join(', ')} />
                  <InfoRow label="Aktuelle URL" value={browserInfo.currentURL} />
                  <InfoRow label="Referrer (Herkunftsseite)" value={browserInfo.referrer} />
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <List dense>
                  <InfoRow label="Zeitpunkt des Zugriffs" value={browserInfo.currentTime} />
                  <InfoRow label="Verbindungsart" value={browserInfo.connectionType} />
                  {connectionInfo && (
                    <>
                      <InfoRow label="Netzwerk-Typ" value={connectionInfo.effectiveType} />
                      <InfoRow label="Download-Geschwindigkeit" value={connectionInfo.downlink !== 'Nicht verfügbar' ? `${connectionInfo.downlink} Mbps` : connectionInfo.downlink} />
                      <InfoRow label="Round-Trip-Time" value={connectionInfo.rtt !== 'Nicht verfügbar' ? `${connectionInfo.rtt} ms` : connectionInfo.rtt} />
                      <InfoRow label="Datensparmodus" value={connectionInfo.saveData ? 'Aktiviert' : 'Deaktiviert'} chip={connectionInfo.saveData} />
                    </>
                  )}
                </List>
              </Grid>
            </Grid>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>IP-Adresse:</strong> Ihre IP-Adresse wird bei jedem Seitenaufruf übertragen und kann 
                Ihren ungefähren Standort (Land, Stadt, Provider) verraten. Diese kann nicht direkt im Browser 
                abgefragt werden, wird aber automatisch an den Server gesendet.
              </Typography>
            </Alert>
          </InfoCard>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* 2. Browser- und Geräteinformationen */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">2. Browser- und Geräteinformationen</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ color: '#ffffff' }}>
              <InfoCard 
            title="2. Browser- und Geräteinformationen" 
            icon={ComputerIcon} 
            color="secondary"
          >
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Diese können passiv durch Skripte oder Stilanalyse ausgelesen werden, ohne dass Sie aktiv etwas eingeben
              </Typography>
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                  Bildschirm & Display
                </Typography>
                <List dense>
                  <InfoRow label="Bildschirmauflösung" value={`${browserInfo.screenWidth} × ${browserInfo.screenHeight} px`} />
                  <InfoRow label="Verfügbare Breite" value={`${browserInfo.availWidth} px`} />
                  <InfoRow label="Verfügbare Höhe" value={`${browserInfo.availHeight} px`} />
                  <InfoRow label="Fenstergröße" value={`${browserInfo.windowWidth} × ${browserInfo.windowHeight} px`} />
                  <InfoRow label="Farbtiefe" value={`${browserInfo.screenColorDepth} Bit`} />
                  <InfoRow label="Pixel-Tiefe" value={`${browserInfo.screenPixelDepth} Bit`} />
                  <InfoRow label="Pixel-Verhältnis (DPI)" value={browserInfo.devicePixelRatio.toString()} />
                  <InfoRow label="Orientierung" value={browserInfo.orientation} />
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                  Hardware & Gerät
                </Typography>
                <List dense>
                  <InfoRow label="Betriebssystem" value={browserInfo.platform} />
                  <InfoRow label="CPU-Kerne" value={browserInfo.hardwareConcurrency.toString()} />
                  <InfoRow 
                    label="Gerätespeicher" 
                    value={
                      browserInfo.deviceMemory === 'Nicht verfügbar' 
                        ? browserInfo.deviceMemory 
                        : `${browserInfo.deviceMemory} GB`
                    } 
                  />
                  <InfoRow 
                    label="Touch-Unterstützung" 
                    value={browserInfo.touchSupport ? `Ja (${browserInfo.maxTouchPoints} Punkte)` : 'Nein'} 
                    chip={browserInfo.touchSupport}
                  />
                  <InfoRow 
                    label="Battery API" 
                    value={browserInfo.batteryAPI ? 'Verfügbar' : 'Nicht verfügbar'} 
                    chip={browserInfo.batteryAPI}
                  />
                  <InfoRow 
                    label="Geolocation API" 
                    value={browserInfo.geolocationAPI ? 'Verfügbar (erfordert Zustimmung)' : 'Nicht verfügbar'} 
                    chip={browserInfo.geolocationAPI}
                  />
                  <InfoRow label="Locale" value={browserInfo.locale} />
                  <InfoRow label="Nummernsystem" value={browserInfo.numberingSystem} />
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                  Zeitzone & Zeit
                </Typography>
                <List dense>
                  <InfoRow label="Zeitzone" value={browserInfo.timezone} />
                  <InfoRow label="Zeitzone-Offset" value={`${-browserInfo.timezoneOffset / 60} Stunden`} />
                  <InfoRow label="Systemzeit" value={browserInfo.currentTime} />
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                  Installierte Schriftarten
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {fonts.map((font, idx) => (
                    <Chip 
                      key={idx} 
                      label={font} 
                      size="small" 
                      variant="outlined"
                      sx={{
                        color: '#42a5f5',
                        borderColor: '#42a5f5',
                        '& .MuiChip-label': {
                          color: '#42a5f5',
                        },
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {fonts.length} Schriftarten erkannt
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Font-Enumeration:</strong> {fonts.length > 0 ? 'Möglich' : 'Nicht verfügbar'} - 
                    Die Liste der installierten Schriftarten kann zur Fingerprint-Erstellung verwendet werden.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Hinweis:</strong> Browser-Plug-ins werden von modernen Browsern aus Sicherheitsgründen 
                nicht mehr preisgegeben. Dies war eine wichtige Verbesserung für den Datenschutz.
              </Typography>
            </Alert>
          </InfoCard>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* 3. Cookies, LocalStorage & ähnliche Mechanismen */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">3. Cookies, LocalStorage & ähnliche Mechanismen</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ color: '#ffffff' }}>
              <InfoCard 
            title="3. Cookies, LocalStorage & ähnliche Mechanismen" 
            icon={StorageIcon} 
            color="warning"
          >
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Diese sind zwar "lokal gespeichert", werden aber automatisch übertragen oder ausgelesen, 
                sobald Sie die Seite aufrufen
              </Typography>
            </Alert>
            {storageInfo && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <InfoRow 
                      label="Cookies aktiviert" 
                      value={browserInfo.cookiesEnabled ? 'Ja' : 'Nein'} 
                      chip={browserInfo.cookiesEnabled}
                    />
                    <InfoRow 
                      label="LocalStorage verfügbar" 
                      value={storageInfo.localStorageAvailable ? 'Ja' : 'Nein'} 
                      chip={storageInfo.localStorageAvailable}
                    />
                    <InfoRow 
                      label="LocalStorage Größe" 
                      value={storageInfo.localStorageSize > 0 ? `${storageInfo.localStorageSize} Bytes` : 'Leer'} 
                    />
                    <InfoRow 
                      label="SessionStorage verfügbar" 
                      value={storageInfo.sessionStorageAvailable ? 'Ja' : 'Nein'} 
                      chip={storageInfo.sessionStorageAvailable}
                    />
                    <InfoRow 
                      label="SessionStorage Größe" 
                      value={storageInfo.sessionStorageSize > 0 ? `${storageInfo.sessionStorageSize} Bytes` : 'Leer'} 
                    />
                    <InfoRow 
                      label="SessionStorage Keys" 
                      value={storageInfo.sessionStorageKeys || 0} 
                    />
                    <InfoRow 
                      label="Cookie-Anzahl" 
                      value={storageInfo.cookieCount || 0} 
                    />
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <InfoRow 
                      label="IndexedDB verfügbar" 
                      value={storageInfo.indexedDBAvailable ? 'Ja' : 'Nein'} 
                      chip={storageInfo.indexedDBAvailable}
                    />
                    <InfoRow 
                      label="Cache API verfügbar" 
                      value={storageInfo.cacheAvailable ? 'Ja' : 'Nein'} 
                      chip={storageInfo.cacheAvailable}
                    />
                    <InfoRow 
                      label="Service Workers verfügbar" 
                      value={storageInfo.serviceWorkersAvailable ? 'Ja' : 'Nein'} 
                      chip={storageInfo.serviceWorkersAvailable}
                    />
                    {storageInfo.cookies && storageInfo.cookies !== 'Keine' && (
                      <InfoRow 
                        label="Cookies (Vorschau)" 
                        value={storageInfo.cookies} 
                      />
                    )}
                  </List>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>ETag & Cache-Header:</strong> Diese werden automatisch vom Browser gesendet und 
                      können zur Wiedererkennung über Browser-Caching verwendet werden.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            )}
          </InfoCard>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* 4. Standortdaten */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">4. Standortdaten</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ color: '#ffffff' }}>
              <InfoCard 
            title="4. Standortdaten" 
            icon={LocationIcon} 
            color="error"
          >
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Ohne aktive Zustimmung kann kein genauer GPS-Standort abgefragt werden. 
                Die IP-Adresse wird jedoch automatisch übertragen und kann zur Standortbestimmung verwendet werden.
              </Typography>
            </Alert>
            
            {locationLoading ? (
              <Typography variant="body2" sx={{ color: '#ffffff' }}>
                Lade Standortdaten...
              </Typography>
            ) : locationError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">{locationError}</Typography>
              </Alert>
            ) : locationData ? (
              <List dense>
                <InfoRow label="IP-Adresse" value={locationData.ip || 'Nicht verfügbar'} />
                {locationData.location && (
                  <>
                    <InfoRow label="Land" value={locationData.location.country || 'Nicht verfügbar'} />
                    <InfoRow label="Ländercode" value={locationData.location.country_code || 'Nicht verfügbar'} />
                    <InfoRow label="Bundesland/Staat" value={locationData.location.state || 'Nicht verfügbar'} />
                    <InfoRow label="Stadt" value={locationData.location.city || 'Nicht verfügbar'} />
                    <InfoRow label="Postleitzahl" value={locationData.location.zip || 'Nicht verfügbar'} />
                    <InfoRow 
                      label="Koordinaten" 
                      value={
                        locationData.location.latitude && locationData.location.longitude
                          ? `${locationData.location.latitude.toFixed(4)}, ${locationData.location.longitude.toFixed(4)}`
                          : 'Nicht verfügbar'
                      } 
                    />
                    <InfoRow label="Zeitzone" value={locationData.location.timezone || browserInfo.timezone} />
                    <InfoRow label="Lokale Zeit" value={locationData.location.local_time || 'Nicht verfügbar'} />
                    <InfoRow label="Währung" value={locationData.location.currency_code || 'Nicht verfügbar'} />
                    <InfoRow label="Kontinent" value={locationData.location.continent || 'Nicht verfügbar'} />
                  </>
                )}
                {locationData.asn && (
                  <>
                    <InfoRow label="ASN" value={locationData.asn.asn || 'Nicht verfügbar'} />
                    <InfoRow label="Provider" value={locationData.asn.org || 'Nicht verfügbar'} />
                    <InfoRow label="ASN-Beschreibung" value={locationData.asn.descr || 'Nicht verfügbar'} />
                  </>
                )}
                {locationData.company && (
                  <>
                    <InfoRow label="Firmenname" value={locationData.company.name || 'Nicht verfügbar'} />
                    <InfoRow label="Firmen-Typ" value={locationData.company.type || 'Nicht verfügbar'} />
                    <InfoRow label="Domain" value={locationData.company.domain || 'Nicht verfügbar'} />
                  </>
                )}
                <InfoRow 
                  label="Ist VPN" 
                  value={locationData.is_vpn ? 'Ja' : 'Nein'} 
                  chip={locationData.is_vpn}
                />
                <InfoRow 
                  label="Ist Proxy" 
                  value={locationData.is_proxy ? 'Ja' : 'Nein'} 
                  chip={locationData.is_proxy}
                />
                <InfoRow 
                  label="Ist Datacenter" 
                  value={locationData.is_datacenter ? 'Ja' : 'Nein'} 
                  chip={locationData.is_datacenter}
                />
                {locationData.is_datacenter && locationData.datacenter && (
                  <InfoRow label="Datacenter" value={locationData.datacenter.datacenter || 'Nicht verfügbar'} />
                )}
              </List>
            ) : null}
            
            <List dense sx={{ mt: 2 }}>
              <InfoRow label="Browser-Zeitzone" value={browserInfo.timezone} />
              <InfoRow label="Sprachregion" value={browserInfo.language} />
            </List>
            
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Hinweis:</strong> Diese Standortdaten wurden über die IP-Adresse ermittelt, die bei jedem 
                Seitenaufruf automatisch übertragen wird. Die Genauigkeit variiert und kann auf Stadt- oder 
                Provider-Ebene liegen. Die Daten wurden von der API{' '}
                <a href="https://de.ipapi.is/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                  de.ipapi.is
                </a>{' '}
                bereitgestellt.
              </Typography>
            </Alert>
          </InfoCard>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* 5. Verhaltensdaten */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">5. Verhaltensdaten</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ color: '#ffffff' }}>
              <InfoCard 
            title="5. Verhaltensdaten" 
            icon={PsychologyIcon} 
            color="info"
          >
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Manche Seiten messen auch ohne aktives Zutun
              </Typography>
            </Alert>
            {behaviorInfo ? (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                      Basis-Verhaltensdaten
                    </Typography>
                    <List dense>
                      <InfoRow label="Seitenladezeit" value={`${behaviorInfo.loadTime} ms`} />
                      <InfoRow label="DOM-Ready-Zeit" value={`${behaviorInfo.domReady} ms`} />
                      <InfoRow 
                        label="Seite sichtbar" 
                        value={behaviorInfo.isVisible ? 'Ja' : 'Nein'} 
                        chip={behaviorInfo.isVisible}
                      />
                      <InfoRow 
                        label="Page Visibility API" 
                        value={behaviorInfo.pageVisibilityAvailable ? 'Verfügbar' : 'Nicht verfügbar'} 
                        chip={behaviorInfo.pageVisibilityAvailable}
                      />
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                      Performance-Timing
                    </Typography>
                    <List dense>
                      {behaviorInfo.navigationStart && (
                        <InfoRow label="Navigation Start" value={behaviorInfo.navigationStart} />
                      )}
                      {behaviorInfo.domContentLoaded && (
                        <InfoRow label="DOM Content Loaded" value={`${behaviorInfo.domContentLoaded} ms`} />
                      )}
                      {behaviorInfo.pageLoad && (
                        <InfoRow label="Page Load" value={`${behaviorInfo.pageLoad} ms`} />
                      )}
                      {behaviorInfo.dnsLookup && (
                        <InfoRow label="DNS Lookup" value={`${behaviorInfo.dnsLookup} ms`} />
                      )}
                      {behaviorInfo.tcpConnect && (
                        <InfoRow label="TCP Connect" value={`${behaviorInfo.tcpConnect} ms`} />
                      )}
                      {behaviorInfo.serverResponse && (
                        <InfoRow label="Server Response" value={`${behaviorInfo.serverResponse} ms`} />
                      )}
                    </List>
                  </Grid>
                </Grid>
                {(behaviorInfo.jsHeapSize || behaviorInfo.jsHeapLimit || behaviorInfo.scrollDepth !== undefined) && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Erweiterte Metriken
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          {behaviorInfo.jsHeapSize && (
                            <InfoRow label="JS Heap Size" value={behaviorInfo.jsHeapSize} />
                          )}
                          {behaviorInfo.jsHeapLimit && (
                            <InfoRow label="JS Heap Limit" value={behaviorInfo.jsHeapLimit} />
                          )}
                          {behaviorInfo.scrollDepth !== undefined && (
                            <InfoRow label="Scroll-Tiefe" value={`${behaviorInfo.scrollDepth}%`} />
                          )}
                        </List>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <List dense>
                          {behaviorInfo.timeOnPage && (
                            <InfoRow label="Verweildauer" value={`${behaviorInfo.timeOnPage} Sekunden`} />
                          )}
                          {behaviorInfo.pageHidden !== undefined && (
                            <InfoRow 
                              label="Page Hidden" 
                              value={behaviorInfo.pageHidden === true ? 'Ja' : behaviorInfo.pageHidden === false ? 'Nein' : behaviorInfo.pageHidden} 
                              chip={behaviorInfo.pageHidden === false}
                            />
                          )}
                          {behaviorInfo.visibilityState && (
                            <InfoRow label="Visibility State" value={behaviorInfo.visibilityState} />
                          )}
                          {behaviorInfo.performanceAPI && (
                            <InfoRow 
                              label="Performance API" 
                              value={typeof behaviorInfo.performanceAPI === 'string' ? behaviorInfo.performanceAPI : (behaviorInfo.performanceAPI ? 'Verfügbar' : 'Nicht verfügbar')} 
                              chip={behaviorInfo.performanceAPI === 'Verfügbar' || behaviorInfo.performanceAPI === true}
                            />
                          )}
                          {behaviorInfo.performanceObserver && (
                            <InfoRow 
                              label="Performance Observer" 
                              value={typeof behaviorInfo.performanceObserver === 'string' ? behaviorInfo.performanceObserver : (behaviorInfo.performanceObserver ? 'Verfügbar' : 'Nicht verfügbar')} 
                              chip={behaviorInfo.performanceObserver === 'Verfügbar' || behaviorInfo.performanceObserver === true}
                            />
                          )}
                        </List>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ color: '#ffffff' }}>
                Lade Verhaltensdaten...
              </Typography>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Weitere mögliche Messungen:</strong> Scrolltiefe, Mausbewegungen, Fokusänderungen 
                (wenn JavaScript aktiv), Verweildauer auf der Seite
              </Typography>
            </Alert>
          </InfoCard>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* 6. Erweiterte Techniken */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">6. Erweiterte Techniken</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ color: '#ffffff' }}>
              <InfoCard 
            title="6. Erweiterte Techniken" 
            icon={WarningIcon} 
            color="error"
          >
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Manche Websites gehen weiter und verwenden fortgeschrittene Fingerprinting-Techniken
              </Typography>
            </Alert>
            
            {/* Canvas Fingerprint */}
            {canvasFingerprint && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Canvas-Fingerprinting
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff' }} paragraph>
                  Winzige Grafik wird gerendert → charakteristische Unterschiede zwischen Geräten
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                  <Box
                    component="img"
                    src={canvasFingerprint}
                    alt="Canvas Fingerprint"
                    sx={{
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      maxWidth: '200px',
                    }}
                  />
                  <Box>
                    <Typography variant="caption" display="block" gutterBottom>
                      <strong>Hash:</strong> {canvasFingerprint.substring(0, 50)}...
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#ffffff' }}>
                      Diese Grafik ist für Ihren Browser einzigartig
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* WebGL Fingerprint */}
            {webglInfo && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  WebGL-Fingerprinting (Grafikkarte)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <List dense>
                      <InfoRow label="Vendor" value={webglInfo.vendor} />
                      <InfoRow label="Renderer" value={webglInfo.renderer} />
                      <InfoRow label="Version" value={webglInfo.version} />
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <List dense>
                      <InfoRow label="Shader-Sprache" value={webglInfo.shadingLanguageVersion} />
                      <InfoRow label="Grafikkarten-Hersteller" value={webglInfo.debugVendor} />
                      <InfoRow label="Grafikkarten-Modell" value={webglInfo.debugRenderer} />
                    </List>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* AudioContext Fingerprint */}
            {audioFingerprint && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  AudioContext-Fingerprinting
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff' }} paragraph>
                  Klangsignatur der Soundkarte
                </Typography>
                <Typography variant="body2">
                  <strong>Fingerprint:</strong> {audioFingerprint.substring(0, 50)}...
                </Typography>
              </Box>
            )}

            {/* WebRTC Leak Warning */}
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>WebRTC-Leak:</strong> WebRTC kann IP-Adressen aus lokalen Netzwerken offenbaren, 
                auch wenn Sie einen VPN verwenden. Dies kann Ihre Privatsphäre gefährden.
              </Typography>
            </Alert>
          </InfoCard>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Client Hints */}
        {clientHints && (
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">7. User-Agent Client Hints (UA-CH)</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <InfoCard 
                  title="7. User-Agent Client Hints (UA-CH)" 
                  icon={InfoIcon} 
                  color="info"
                >
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Moderne Browser-API, die detaillierte Informationen über Browser und Gerät preisgibt
                    </Typography>
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <List dense>
                        {Object.entries(clientHints).slice(0, Math.ceil(Object.keys(clientHints).length / 2)).map(([key, value]) => (
                          <InfoRow key={key} label={key} value={value} />
                        ))}
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <List dense>
                        {Object.entries(clientHints).slice(Math.ceil(Object.keys(clientHints).length / 2)).map(([key, value]) => (
                          <InfoRow key={key} label={key} value={value} />
                        ))}
                      </List>
                    </Grid>
                  </Grid>
                </InfoCard>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

      </Grid>
        </Container>
        </Box>
      )}
    </>
  );
};

export default BrowserInfo;


































